import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  CheckCircleIcon,
  UserPlusIcon,
  UserCircleIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  BookOpenIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { api } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { toastError, toastSuccess } from "../utils/toast";
import { filterAndSortSpeakers, getOtherOutlineOptions, getSpeakerOutlineOptions } from "../utils/publicTalkSpeakers";
import plannerIcon from "../assets/logo/Planner-Turni-icon-192x192.png";
import PublicTalkAppointmentTimes, { appointmentTimesPayload } from "../components/PublicTalkAppointmentTimes";

const tabs = [
  ["/discorsi-pubblici/entrata", "Oratori in entrata", UserPlusIcon],
  ["/discorsi-pubblici/uscita", "Oratori in uscita", UserCircleIcon],
  ["/discorsi-pubblici/calendario", "Calendario", CalendarDaysIcon],
  ["/discorsi-pubblici/approvazioni", "Approvazioni", CheckCircleIcon],
  ["/discorsi-pubblici/schemi", "Catalogo schemi", BookOpenIcon],
  ["/discorsi-pubblici/impostazioni", "Impostazioni", Cog6ToothIcon],
];

const formatDate = (value) =>
  value
    ? new Date(`${String(value).slice(0, 10)}T12:00:00`).toLocaleDateString(
        "it-IT",
        { weekday: "short", day: "2-digit", month: "long", year: "numeric" }
      )
    : "";

const feedbackMeta = {
  1: { label: "Rosso", className: "bg-red-100 text-red-800" },
  2: { label: "Giallo", className: "bg-amber-100 text-amber-800" },
  3: { label: "Verde", className: "bg-green-100 text-green-800" },
};

const feedbackFromAverage = (average) => {
  const numeric = Number(average);
  return Number.isFinite(numeric) && numeric > 0
    ? feedbackMeta[Math.max(1, Math.min(3, Math.round(numeric)))]
    : null;
};

const directionMeta = {
  outgoing: { label: "In uscita", className: "border-orange-200 bg-orange-50 text-orange-900" },
  incoming: { label: "In entrata", className: "border-sky-200 bg-sky-50 text-sky-900" },
  local: { label: "Locale", className: "border-slate-200 bg-slate-50 text-slate-800" },
};

const preferredSharedAreaId = (areas) =>
  areas.find((area) =>
    area.stato === "active" && String(area.nome || "").trim().toLocaleLowerCase("it") === "sicilia 11"
  )?.id || areas.find((area) => area.stato === "active")?.id || areas[0]?.id || "";

const parseOutlineNumbers = (value) =>
  [...new Set(String(value || "").split(/[,;\s]+/).map(Number).filter(Number.isInteger))];

const PublicTalkSearchInput = ({ label, value, onChange, placeholder, ariaLabel, className = "" }) => {
  const inputId = `public-talk-search-${label.toLocaleLowerCase("it").replace(/[^a-z0-9]+/g, "-")}`;

  return (
    <div className={`group relative block min-w-0 pt-2 ${className}`}>
      <label htmlFor={inputId} className="absolute left-3 top-0 z-10 cursor-text rounded bg-white px-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-primary-700 shadow-[0_0_0_3px_white]">
        {label}
      </label>
      <MagnifyingGlassIcon
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-[35%] text-primary-600 transition-colors group-focus-within:text-primary-700"
      />
      <input
        id={inputId}
        className="form-input h-12 w-full border-slate-200 bg-white pl-11 pr-3 text-base text-slate-800 shadow-sm transition placeholder:font-normal placeholder:text-slate-400 hover:border-primary-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        aria-label={ariaLabel}
      />
      <span className="pointer-events-none absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-primary-300 transition-colors duration-200 group-hover:bg-primary-500 group-focus-within:bg-primary-600" />
    </div>
  );
};

const monthKeyFromDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const monthDateFromKey = (key) => {
  const [year, month] = String(key || "").split("-").map(Number);
  return Number.isInteger(year) && Number.isInteger(month)
    ? new Date(year, month - 1, 1)
    : new Date(new Date().getFullYear(), new Date().getMonth(), 1);
};

const dateKeyFromDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const addMonths = (date, amount) =>
  new Date(date.getFullYear(), date.getMonth() + amount, 1);

const formatMonth = (date) =>
  new Intl.DateTimeFormat("it-IT", { month: "long", year: "numeric" }).format(date);

const getWeekendDaysForMonth = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const days = [];
  const lastDay = new Date(year, month + 1, 0).getDate();
  for (let day = 1; day <= lastDay; day += 1) {
    const date = new Date(year, month, day);
    if (date.getDay() === 6 || date.getDay() === 0) {
      days.push({ key: dateKeyFromDate(date), day, weekday: date.getDay() });
    }
  }
  if (days[0]?.weekday === 0) days.unshift({ empty: true, key: `empty-start-${monthKeyFromDate(monthDate)}` });
  if (days[days.length - 1]?.weekday === 6) days.push({ empty: true, key: `empty-end-${monthKeyFromDate(monthDate)}` });
  return days;
};

const calendarFilterOptions = [
  ["month", "Mensile"],
  ["quarter", "Trimestrale"],
  ["semester", "Semestrale"],
  ["year", "Annuale"],
  ["specific", "Data specifica"],
];

const getCalendarRange = (monthDate, filter, specificDate) => {
  if (filter === "specific" && /^\d{4}-\d{2}-\d{2}$/.test(specificDate)) {
    return { start: specificDate, end: specificDate, label: formatDate(specificDate) };
  }

  let start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  let end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

  if (filter === "quarter") {
    // I filtri estesi partono dal mese mostrato, non dall'inizio del trimestre solare.
    end = new Date(start.getFullYear(), start.getMonth() + 3, 0);
  } else if (filter === "semester") {
    end = new Date(start.getFullYear(), start.getMonth() + 6, 0);
  } else if (filter === "year") {
    start = new Date(monthDate.getFullYear(), 0, 1);
    end = new Date(monthDate.getFullYear(), 11, 31);
  }

  const startKey = dateKeyFromDate(start);
  const endKey = dateKeyFromDate(end);
  return {
    start: startKey,
    end: endKey,
    label:
      filter === "month"
        ? formatMonth(monthDate)
        : `${formatDate(startKey)} – ${formatDate(endKey)}`,
  };
};

const getApprovalRequestPresentation = (request) => {
  const payload = request.payload || {};
  const speakerName = `${payload.nome || ""} ${payload.cognome || ""}`.trim();
  const origin = request.payload_congregazione_origine || request.speaker_congregazione_origine || request.congregazione_proponente;
  const speakerCount = Array.isArray(payload.speakers) ? payload.speakers.length : 0;

  if (request.tipo === "add_speaker") {
    return {
      title: `Nuovo oratore · ${speakerName || "Nome non indicato"}`,
      detail: `Provenienza: ${origin || "congregazione non indicata"} · ${payload.qualifica === "anziano" ? "Anziano" : "Servitore di ministero"} · ${(payload.outline_ids || []).length} schemi proposti`,
    };
  }
  if (request.tipo === "bulk_add_speakers") {
    return {
      title: `Importazione oratori · ${speakerCount} ${speakerCount === 1 ? "oratore" : "oratori"}`,
      detail: `Provenienza: ${origin || "congregazione non indicata"}`,
    };
  }
  if (request.tipo === "update_speaker") {
    return { title: `Modifica oratore · ${speakerName || "Nome non indicato"}`, detail: `Provenienza: ${origin || "congregazione non indicata"}` };
  }
  if (request.tipo === "remove_speaker") {
    const name = request.speaker_nome || `${payload.speaker_nome || ""} ${payload.speaker_cognome || ""}`.trim();
    return { title: `Rimozione oratore · ${name || "Nome non disponibile"}`, detail: `Provenienza: ${origin || "congregazione non indicata"}` };
  }
  if (request.tipo === "create_area") {
    return { title: `Nuova area condivisa · ${payload.nome || request.area_nome || "Nome non indicato"}`, detail: "Richiede la revisione di un altro amministratore." };
  }
  return { title: `Richiesta da esaminare · ${request.area_nome || payload.nome || "Area non indicata"}`, detail: "Dettagli disponibili prima dell’approvazione." };
};

const DiscorsiPubblici = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, activeCongregazione, setActiveCongregazione } = useAuth();
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [areas, setAreas] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState("");
  const [speakers, setSpeakers] = useState([]);
  const [outlines, setOutlines] = useState([]);
  const [entries, setEntries] = useState([]);
  const [requests, setRequests] = useState([]);
  const [congregations, setCongregations] = useState([]);
  const [coordinatorCandidates, setCoordinatorCandidates] = useState([]);
  const [localSpeakerCandidates, setLocalSpeakerCandidates] = useState([]);
  const [coordinatorIds, setCoordinatorIds] = useState([]);
  const [savedCoordinatorIds, setSavedCoordinatorIds] = useState([]);
  const [coordinatorToAdd, setCoordinatorToAdd] = useState("");
  const [savingCoordinators, setSavingCoordinators] = useState(false);
  const [speakerSearch, setSpeakerSearch] = useState("");
  const [speakerSort, setSpeakerSort] = useState("default");
  const [outlineSearch, setOutlineSearch] = useState("");
  const [outlineSpeakersModal, setOutlineSpeakersModal] = useState(null);
  const [outlineSpeakerSort, setOutlineSpeakerSort] = useState("least_recent");
  const [suggestions, setSuggestions] = useState([]);
  const [feedbackDrafts, setFeedbackDrafts] = useState({});
  const [calendarMonthKey, setCalendarMonthKey] = useState(() => monthKeyFromDate(new Date()));
  const [calendarFilter, setCalendarFilter] = useState("month");
  const [calendarSpecificDate, setCalendarSpecificDate] = useState("");
  const [selectedCalendarEntry, setSelectedCalendarEntry] = useState(null);
  const [calendarEntryEditing, setCalendarEntryEditing] = useState(false);
  const [calendarEntryForm, setCalendarEntryForm] = useState(null);
  const [calendarNewAppointment, setCalendarNewAppointment] = useState(null);
  const [calendarNewAppointmentOriginCongregationId, setCalendarNewAppointmentOriginCongregationId] = useState("");
  const [appointmentSpeaker, setAppointmentSpeaker] = useState(null);
  const [appointmentForm, setAppointmentForm] = useState({
    outline_id: "", data: "", congregazione_ospitante_id: "", cantico_suggerito: "",
  });
  const [historyModal, setHistoryModal] = useState(null);
  const [editSpeakerModal, setEditSpeakerModal] = useState(null);
  const [editOutlineToAdd, setEditOutlineToAdd] = useState("");
  const [areaName, setAreaName] = useState("");
  const [importCode, setImportCode] = useState("");
  const [saving, setSaving] = useState(false);
  const importFileRef = useRef(null);
  const [speakerForm, setSpeakerForm] = useState({
    congregazione_origine_id: "", volontario_id: "", nome: "", cognome: "", email: "",
    telefono: "", qualifica: "anziano", outline_numbers: "",
  });
  const [scheduleForm, setScheduleForm] = useState({
    speaker_id: "", direzione: "incoming", congregazione_ospitante_id: "",
    outline_id: "",
    data: "",
    cantico_suggerito: "",
  });
  const [scheduleOriginCongregationId, setScheduleOriginCongregationId] = useState("");

  const section = location.pathname.endsWith("/impostazioni")
    ? "settings"
    : location.pathname.endsWith("/schemi")
    ? "outlines"
    : location.pathname.endsWith("/calendario")
    ? "calendar"
    : location.pathname.endsWith("/approvazioni")
    ? "approvals"
    : "speakers";
  const speakerDirection = location.pathname.endsWith("/uscita") ? "outgoing" : "incoming";
  const approvalCongregationId = Number(
    new URLSearchParams(location.search).get("congregazione_id")
  );
  const shouldSwitchApprovalCongregation =
    section === "approvals" &&
    user?.ruolo === "super_admin" &&
    Number.isInteger(approvalCongregationId) &&
    approvalCongregationId > 0 &&
    Number(activeCongregazione?.id) !== approvalCongregationId;

  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      if (section === "calendar") {
        const response = await api.get("/discorsi-pubblici/calendar-bootstrap");
        const data = response.data || {};
        if (!data.canManage) {
          navigate("/", { replace: true });
          return;
        }
        const nextAreas = data.areas || [];
        setAccess({
          congregation: data.congregation,
          isAdmin: data.isAdmin,
          isCoordinator: data.isCoordinator,
          isSuperAdmin: data.isSuperAdmin,
          canManage: data.canManage,
        });
        setAreas(nextAreas);
        setSelectedAreaId((current) =>
          current && nextAreas.some((area) => String(area.id) === String(current))
            ? current
            : preferredSharedAreaId(nextAreas)
        );
        setOutlines(data.outlines || []);
        setEntries(data.entries || []);
        setCongregations(data.congregations || []);
        return;
      }

      const accessResponse = await api.get("/discorsi-pubblici/access");
      if (!accessResponse.data?.canManage) {
        navigate("/", { replace: true });
        return;
      }
      setAccess(accessResponse.data);
      const baseRequests = [
        api.get("/discorsi-pubblici/areas"),
        api.get("/discorsi-pubblici/outlines"),
      ];
      if (section === "speakers" || section === "calendar") {
        baseRequests.push(api.get("/discorsi-pubblici/congregations"));
      }
      if (section === "speakers") {
        baseRequests.push(api.get("/discorsi-pubblici/local-speaker-candidates"));
      }
      if (accessResponse.data?.isAdmin && section === "settings") {
        baseRequests.push(
          api.get("/discorsi-pubblici/coordinators"),
          api.get("/discorsi-pubblici/coordinator-candidates")
        );
      }

      const responses = await Promise.all(baseRequests);
      const [areasResponse, outlinesResponse] = responses;
      let responseIndex = 2;
      const congregationsResponse = (section === "speakers" || section === "calendar")
        ? responses[responseIndex++]
        : null;
      const localCandidatesResponse = section === "speakers"
        ? responses[responseIndex++]
        : null;
      const coordinatorsResponse = accessResponse.data?.isAdmin && section === "settings"
        ? responses[responseIndex++]
        : null;
      const candidatesResponse = accessResponse.data?.isAdmin && section === "settings"
        ? responses[responseIndex++]
        : null;
      const nextAreas = areasResponse.data?.areas || [];
      setAreas(nextAreas);
      setSelectedAreaId((current) =>
        current && nextAreas.some((area) => String(area.id) === String(current))
          ? current
          : preferredSharedAreaId(nextAreas)
      );
      setOutlines(outlinesResponse.data?.outlines || []);
      if (congregationsResponse) {
        setCongregations(congregationsResponse.data?.congregations || []);
      }
      if (localCandidatesResponse) {
        setLocalSpeakerCandidates(localCandidatesResponse.data?.volunteers || []);
      }
      if (coordinatorsResponse && candidatesResponse) {
        const currentCoordinatorIds = (coordinatorsResponse.data?.coordinators || [])
          .map((item) => Number(item.volontario_id));
        setCoordinatorIds(currentCoordinatorIds);
        setSavedCoordinatorIds(currentCoordinatorIds);
        setCoordinatorToAdd("");
        setCoordinatorCandidates(candidatesResponse.data?.volunteers || []);
      }
    } catch (error) {
      toastError(error.response?.data?.message || "Sezione non disponibile");
      navigate("/", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate, section]);

  const loadSpeakers = useCallback(async () => {
    if (!selectedAreaId) {
      setSpeakers([]);
      return;
    }
    try {
      const response = await api.get(
        `/discorsi-pubblici/areas/${selectedAreaId}/speakers`
      );
      setSpeakers(response.data?.speakers || []);
    } catch (error) {
      toastError(error.response?.data?.message || "Impossibile caricare gli oratori");
    }
  }, [selectedAreaId]);

  const loadSchedule = useCallback(async () => {
    try {
      const response = await api.get("/discorsi-pubblici/schedule");
      const nextEntries = response.data?.entries || [];
      setEntries(nextEntries);
      return nextEntries;
    } catch (error) {
      toastError(error.response?.data?.message || "Impossibile caricare il calendario");
      return [];
    }
  }, []);

  const loadRequests = useCallback(async () => {
    try {
      const response = await api.get("/discorsi-pubblici/changes");
      setRequests(response.data?.requests || []);
    } catch (error) {
      toastError(error.response?.data?.message || "Impossibile caricare le approvazioni");
    }
  }, []);

  useEffect(() => {
    if (!shouldSwitchApprovalCongregation) loadBase();
  }, [loadBase, shouldSwitchApprovalCongregation]);
  useEffect(() => {
    if (!shouldSwitchApprovalCongregation) return;
    let cancelled = false;
    api
      .get("/congregazioni")
      .then((response) => {
        const target = (response.data || []).find(
          (congregation) => Number(congregation.id) === approvalCongregationId
        );
        if (!cancelled && target) {
          setActiveCongregazione(target);
        } else if (!cancelled) {
          toastError("La congregazione indicata nella richiesta non è disponibile");
          loadBase();
        }
      })
      .catch(() => {
        if (!cancelled) {
          toastError("Impossibile aprire la congregazione indicata nella richiesta");
          loadBase();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [approvalCongregationId, loadBase, setActiveCongregazione, shouldSwitchApprovalCongregation]);
  useEffect(() => {
    if (!loading && (section === "speakers" || section === "calendar")) {
      loadSpeakers();
    }
  }, [loading, section, loadSpeakers]);
  useEffect(() => {
    if (!loading && section === "approvals") loadRequests();
  }, [loading, section, loadRequests]);
  useEffect(() => {
    const defaultAreaId = preferredSharedAreaId(areas);
    if (!defaultAreaId) return;
    setSelectedAreaId((current) =>
      current && areas.some(
        (area) => area.stato === "active" && String(area.id) === String(current)
      )
        ? String(current)
        : String(defaultAreaId)
    );
  }, [areas]);
  useEffect(() => {
    if (section === "speakers" && speakerDirection === "outgoing" && access?.congregation?.id) {
      setSpeakerForm((current) => ({
        ...current,
        congregazione_origine_id: String(access.congregation.id),
      }));
    }
  }, [access?.congregation?.id, section, speakerDirection]);

  const eligibleSpeakers = useMemo(() => {
    const activeId = Number(access?.congregation?.id);
    return speakers.filter((speaker) => {
      const isInternal = Number(speaker.congregazione_origine_id) === activeId;
      if (scheduleForm.direzione === "outgoing" ? !isInternal : false) return false;
      if (scheduleForm.direzione === "incoming") {
        return !isInternal && Number(speaker.congregazione_origine_id) === Number(scheduleOriginCongregationId);
      }
      return true;
    });
  }, [access?.congregation?.id, scheduleForm.direzione, scheduleOriginCongregationId, speakers]);
  const selectedScheduleSpeaker = useMemo(
    () => speakers.find((speaker) => String(speaker.id) === String(scheduleForm.speaker_id)),
    [scheduleForm.speaker_id, speakers]
  );
  const scheduleOutlineOptions = getSpeakerOutlineOptions(speakers, scheduleForm.speaker_id, outlines);
  const scheduleOtherOutlineOptions = getOtherOutlineOptions(scheduleOutlineOptions, outlines);
  const calendarNewAppointmentEligibleSpeakers = useMemo(() => {
    if (!calendarNewAppointment) return [];
    const activeId = Number(access?.congregation?.id);
    return speakers.filter((speaker) => {
      const isInternal = Number(speaker.congregazione_origine_id) === activeId;
      return calendarNewAppointment.direzione === "incoming"
        ? !isInternal && Number(speaker.congregazione_origine_id) === Number(calendarNewAppointmentOriginCongregationId)
        : isInternal;
    });
  }, [access?.congregation?.id, calendarNewAppointment, calendarNewAppointmentOriginCongregationId, speakers]);
  const calendarNewAppointmentSelectedSpeaker = useMemo(
    () => speakers.find((speaker) => String(speaker.id) === String(calendarNewAppointment?.speaker_id)),
    [calendarNewAppointment?.speaker_id, speakers]
  );
  const calendarNewAppointmentOutlineOptions = useMemo(
    () => getSpeakerOutlineOptions(speakers, calendarNewAppointment?.speaker_id, outlines),
    [calendarNewAppointment?.speaker_id, outlines, speakers]
  );
  const calendarNewAppointmentOtherOutlineOptions = useMemo(
    () => getOtherOutlineOptions(calendarNewAppointmentOutlineOptions, outlines),
    [calendarNewAppointmentOutlineOptions, outlines]
  );
  const calendarEntryEligibleSpeakers = useMemo(() => {
    if (!selectedCalendarEntry) return [];
    const activeId = Number(access?.congregation?.id);
    const eligible = speakers.filter((speaker) => {
      const isInternal = Number(speaker.congregazione_origine_id) === activeId;
      if (selectedCalendarEntry.direzione === "incoming") return !isInternal;
      return isInternal;
    });
    if (eligible.some((speaker) => Number(speaker.id) === Number(selectedCalendarEntry.speaker_id))) {
      return eligible;
    }
    return [{
      id: selectedCalendarEntry.speaker_id,
      nome: selectedCalendarEntry.nome,
      cognome: selectedCalendarEntry.cognome,
      congregazione_origine: selectedCalendarEntry.congregazione_origine,
      outlines: [{
        id: selectedCalendarEntry.outline_id,
        numero: selectedCalendarEntry.numero,
        titolo: selectedCalendarEntry.titolo,
        cantico_suggerito: selectedCalendarEntry.cantico_suggerito,
      }],
    }, ...eligible];
  }, [access?.congregation?.id, selectedCalendarEntry, speakers]);
  const calendarEntryOutlineOptions = useMemo(
    () => getSpeakerOutlineOptions(
      calendarEntryEligibleSpeakers,
      calendarEntryForm?.speaker_id || selectedCalendarEntry?.speaker_id,
      outlines
    ),
    [calendarEntryEligibleSpeakers, calendarEntryForm?.speaker_id, outlines, selectedCalendarEntry?.speaker_id]
  );
  const calendarEntryOtherOutlineOptions = useMemo(
    () => getOtherOutlineOptions(calendarEntryOutlineOptions, outlines),
    [calendarEntryOutlineOptions, outlines]
  );
  const appointmentOtherOutlineOptions = useMemo(
    () => getOtherOutlineOptions(appointmentSpeaker?.outlines || [], outlines),
    [appointmentSpeaker?.outlines, outlines]
  );
  const sharedDestinationCongregations = useMemo(
    () => congregations.filter((item) =>
      Number(item.id) !== Number(access?.congregation?.id) &&
      (item.area_ids || []).some((areaId) => String(areaId) === String(selectedAreaId))
    ),
    [access?.congregation?.id, congregations, selectedAreaId]
  );
  const incomingOriginCongregations = useMemo(() => {
    const externalCongregations = new Map(
      sharedDestinationCongregations.map((item) => [String(item.id), item])
    );
    const activeId = Number(access?.congregation?.id);
    speakers.forEach((speaker) => {
      const originId = Number(speaker.congregazione_origine_id);
      if (originId && originId !== activeId && !externalCongregations.has(String(originId))) {
        externalCongregations.set(String(originId), {
          id: originId,
          nome: speaker.congregazione_origine,
        });
      }
    });
    return Array.from(externalCongregations.values()).sort((left, right) =>
      String(left.nome || "").localeCompare(String(right.nome || ""), "it")
    );
  }, [access?.congregation?.id, sharedDestinationCongregations, speakers]);
  const visibleSpeakers = useMemo(() => {
    return filterAndSortSpeakers({
      speakers,
      activeCongregationId: access?.congregation?.id,
      direction: speakerDirection,
      query: speakerSearch,
      sort: speakerSort,
    });
  }, [access?.congregation?.id, speakerDirection, speakerSearch, speakerSort, speakers]);
  const visibleOutlines = useMemo(() => {
    const normalizedQuery = outlineSearch.trim().toLocaleLowerCase("it");
    if (!normalizedQuery) return outlines;
    return outlines.filter((outline) =>
      `${outline.numero || ""} ${outline.titolo || ""} ${outline.fonte || ""}`
        .toLocaleLowerCase("it")
        .includes(normalizedQuery)
    );
  }, [outlineSearch, outlines]);
  const sortedOutlineSpeakers = useMemo(() => {
    const result = [...(outlineSpeakersModal?.speakers || [])];
    const dateOf = (speaker) => String(speaker.ultima_esecuzione_schema || "").slice(0, 10);
    const alphabetical = (left, right) =>
      `${left.cognome || ""} ${left.nome || ""}`.localeCompare(
        `${right.cognome || ""} ${right.nome || ""}`,
        "it"
      );
    if (outlineSpeakerSort === "alphabetical") return result.sort(alphabetical);
    if (outlineSpeakerSort === "most_recent") {
      return result.sort((left, right) => {
        const comparison = dateOf(right).localeCompare(dateOf(left));
        return comparison || alphabetical(left, right);
      });
    }
    return result.sort((left, right) => {
      const comparison = dateOf(left).localeCompare(dateOf(right));
      return comparison || alphabetical(left, right);
    });
  }, [outlineSpeakerSort, outlineSpeakersModal]);

  const calendarMonthDate = useMemo(
    () => monthDateFromKey(calendarMonthKey),
    [calendarMonthKey]
  );
  const calendarRange = useMemo(
    () => getCalendarRange(calendarMonthDate, calendarFilter, calendarSpecificDate),
    [calendarFilter, calendarMonthDate, calendarSpecificDate]
  );
  const calendarMonths = useMemo(() => {
    if (calendarFilter === "specific") return [calendarMonthDate];
    const start = monthDateFromKey(calendarRange.start.slice(0, 7));
    const end = monthDateFromKey(calendarRange.end.slice(0, 7));
    const months = [];
    for (let cursor = start; cursor <= end; cursor = addMonths(cursor, 1)) {
      months.push(cursor);
    }
    return months.filter((month) => getWeekendDaysForMonth(month).some((day) =>
      !day.empty && day.key >= calendarRange.start && day.key <= calendarRange.end
    ));
  }, [calendarFilter, calendarMonthDate, calendarRange.end, calendarRange.start]);
  const filteredScheduleEntries = useMemo(
    () => entries.filter((entry) => {
      const date = String(entry.data).slice(0, 10);
      return date >= calendarRange.start && date <= calendarRange.end;
    }),
    [calendarRange.end, calendarRange.start, entries]
  );
  const calendarEntriesByDate = useMemo(() => {
    const grouped = new Map();
    entries.forEach((entry) => {
      const date = String(entry.data).slice(0, 10);
      if (date < calendarRange.start || date > calendarRange.end) return;
      const existing = grouped.get(date) || { incoming: [], outgoing: [], local: [] };
      existing[entry.direzione || "incoming"].push(entry);
      grouped.set(date, existing);
    });
    return grouped;
  }, [calendarRange.end, calendarRange.start, entries]);
  const calendarNavigationStep = calendarFilter === "quarter"
    ? 3
    : calendarFilter === "semester"
    ? 6
    : calendarFilter === "year"
    ? 12
    : 1;

  const moveCalendarPeriod = (amount) => {
    setCalendarMonthKey(monthKeyFromDate(addMonths(calendarMonthDate, amount * calendarNavigationStep)));
  };

  const showCurrentPeriod = () => {
    setCalendarMonthKey(monthKeyFromDate(new Date()));
  };

  const createArea = async (event) => {
    event.preventDefault();
    if (!areaName.trim()) return;
    setSaving(true);
    try {
      await api.post("/discorsi-pubblici/areas", { nome: areaName.trim() });
      setAreaName("");
      toastSuccess("Area proposta: attende l'approvazione di un altro admin");
      await loadRequests();
    } catch (error) {
      toastError(error.response?.data?.message || "Creazione non riuscita");
    } finally {
      setSaving(false);
    }
  };

  const importArea = async (event) => {
    event.preventDefault();
    if (!importCode.trim()) return;
    setSaving(true);
    try {
      await api.post("/discorsi-pubblici/areas/import", {
        codice: importCode.trim(),
      });
      setImportCode("");
      toastSuccess("Area importata");
      await loadBase();
    } catch (error) {
      toastError(error.response?.data?.message || "Importazione non riuscita");
    } finally {
      setSaving(false);
    }
  };

  const createSchedule = async (event, { closeCalendarModal = false, form: formOverride = null } = {}) => {
    event.preventDefault();
    const form = formOverride || scheduleForm;
    setSaving(true);
    try {
      await api.post("/discorsi-pubblici/schedule", {
        ...appointmentTimesPayload(form),
        speaker_id: Number(form.speaker_id),
        outline_id: Number(form.outline_id),
        data: form.data,
        cantico_suggerito: form.cantico_suggerito
          ? Number(form.cantico_suggerito)
          : null,
        congregazione_ospitante_id: form.direzione === "outgoing"
          ? Number(form.congregazione_ospitante_id)
          : Number(access?.congregation?.id),
      });
      if (form.data) {
        setCalendarMonthKey(form.data.slice(0, 7));
        setCalendarFilter("month");
      }
      if (!formOverride) {
        setScheduleForm({ speaker_id: "", direzione: scheduleForm.direzione, congregazione_ospitante_id: "", outline_id: "", data: "", cantico_suggerito: "" });
      }
      if (closeCalendarModal) setCalendarNewAppointment(null);
      toastSuccess("Discorso inserito in bozza");
      await loadSchedule();
    } catch (error) {
      toastError(error.response?.data?.message || error.message || "Programmazione non riuscita");
    } finally {
      setSaving(false);
    }
  };

  const proposeSpeaker = async (event) => {
    event.preventDefault();
    const numbers = parseOutlineNumbers(speakerForm.outline_numbers);
    const outlineIds = numbers.map((number) => outlines.find((item) => item.numero === number)?.id);
    if (!selectedAreaId || !numbers.length || outlineIds.some((id) => !id)) {
      toastError("Controlla area e numeri degli schemi");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/discorsi-pubblici/areas/${selectedAreaId}/speaker-proposals`, {
        congregazione_origine_id: Number(speakerForm.congregazione_origine_id),
        volontario_id: speakerForm.volontario_id ? Number(speakerForm.volontario_id) : null,
        nome: speakerForm.nome.trim(), cognome: speakerForm.cognome.trim(),
        email: speakerForm.email.trim() || null, telefono: speakerForm.telefono.trim() || null,
        qualifica: speakerForm.qualifica, outline_ids: outlineIds,
      });
      setSpeakerForm({ congregazione_origine_id: "", volontario_id: "", nome: "", cognome: "", email: "", telefono: "", qualifica: "anziano", outline_numbers: "" });
      toastSuccess(
        speakerDirection === "incoming"
          ? "Oratore proposto: attende l'approvazione di un altro amministratore della stessa congregazione"
          : "Oratore proposto: attende l'approvazione incrociata"
      );
    } catch (error) {
      toastError(error.response?.data?.message || "Proposta non riuscita");
    } finally { setSaving(false); }
  };

  const chooseLocalSpeaker = (id) => {
    const candidate = localSpeakerCandidates.find((item) => String(item.id) === String(id));
    if (!candidate) return;
    setSpeakerForm((current) => ({
      ...current,
      volontario_id: String(candidate.id),
      congregazione_origine_id: String(access?.congregation?.id || ""),
      nome: candidate.nome || "",
      cognome: candidate.cognome || "",
      email: candidate.email || "",
      qualifica: candidate.qualifica || "anziano",
    }));
  };

  const exportSpeakers = async () => {
    if (!selectedAreaId) { toastError("Seleziona prima un'area"); return; }
    try {
      const response = await api.get(`/discorsi-pubblici/areas/${selectedAreaId}/speakers/export`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "oratori-area.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toastError(error.response?.data?.message || "Esportazione non riuscita");
    }
  };

  const downloadDirectoryTemplate = () => {
    const csv = "nome,cognome,email,telefono,qualifica,schemi\nMario,Rossi,mario@example.it,+39 333 0000000,anziano,14;65;113";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "modello-importazione-oratori.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const importSpeakersFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!selectedAreaId || !speakerForm.congregazione_origine_id) {
      toastError("Seleziona area e congregazione di origine prima di importare");
      return;
    }
    try {
      const rows = (await file.text()).replace(/^\uFEFF/, "").trim().split(/\r?\n/);
      const separator = rows[0]?.includes(";") ? ";" : ",";
      const headers = rows.shift().split(separator).map((header) => header.trim().toLowerCase());
      const index = (name) => headers.indexOf(name);
      const required = ["nome", "cognome", "qualifica", "schemi"];
      if (required.some((name) => index(name) < 0)) {
        throw new Error("Il file deve contenere: nome, cognome, qualifica e schemi");
      }
      const imported = rows.filter(Boolean).map((row) => {
        const cells = row.split(separator).map((cell) => cell.trim().replace(/^"|"$/g, ""));
        const qualification = (cells[index("qualifica")] || "").toLowerCase();
        const outlineIds = parseOutlineNumbers(cells[index("schemi")]).map((number) => outlines.find((outline) => outline.numero === number)?.id);
        if (!cells[index("nome")] || !cells[index("cognome")] || outlineIds.some((id) => !id)) {
          throw new Error("Controlla nomi e numeri schemi nel file");
        }
        return {
          nome: cells[index("nome")], cognome: cells[index("cognome")],
          email: index("email") >= 0 ? cells[index("email")] || null : null,
          telefono: index("telefono") >= 0 ? cells[index("telefono")] || null : null,
          qualifica: qualification.includes("servitore") ? "servitore_ministero" : "anziano",
          outline_ids: outlineIds,
        };
      });
      if (!imported.length) throw new Error("Il file non contiene oratori");
      setSaving(true);
      await api.post(`/discorsi-pubblici/areas/${selectedAreaId}/speaker-imports`, {
        congregazione_origine_id: Number(speakerForm.congregazione_origine_id), speakers: imported,
      });
      toastSuccess(
        `${imported.length} oratori proposti: ` +
          (speakerDirection === "incoming"
            ? "attendono l'approvazione di un altro amministratore della stessa congregazione"
            : "attendono l'approvazione incrociata")
      );
    } catch (error) {
      toastError(error.response?.data?.message || error.message || "Importazione non riuscita");
    } finally { setSaving(false); }
  };

  const removeSpeaker = async (speaker) => {
    if (!window.confirm(`Rimuovere ${speaker.nome} ${speaker.cognome} dalla directory? Un altro admin o coordinatore della stessa congregazione dovrà confermare. Gli eventuali incarichi futuri saranno marcati come da sostituire.`)) return;
    setSaving(true);
    try {
      await api.delete(`/discorsi-pubblici/areas/${selectedAreaId}/speakers/${speaker.id}`);
      toastSuccess("Rimozione proposta: attende la conferma nella congregazione d'origine");
      await loadRequests();
    } catch (error) {
      toastError(error.response?.data?.message || "Rimozione non riuscita");
    } finally { setSaving(false); }
  };

  const openAppointment = (speaker) => {
    setAppointmentSpeaker(speaker);
    setAppointmentForm({
      outline_id: "",
      data: "",
      congregazione_ospitante_id: speakerDirection === "incoming"
        ? String(access?.congregation?.id || "")
        : "",
      cantico_suggerito: "",
    });
  };

  const createSpeakerAppointment = async (event) => {
    event.preventDefault();
    if (!appointmentSpeaker) return;
    setSaving(true);
    try {
      await api.post("/discorsi-pubblici/schedule", {
        ...appointmentTimesPayload(appointmentForm),
        speaker_id: Number(appointmentSpeaker.id),
        outline_id: Number(appointmentForm.outline_id),
        data: appointmentForm.data,
        congregazione_ospitante_id: speakerDirection === "incoming"
          ? Number(access?.congregation?.id)
          : Number(appointmentForm.congregazione_ospitante_id),
        cantico_suggerito: appointmentForm.cantico_suggerito
          ? Number(appointmentForm.cantico_suggerito)
          : null,
      });
      setAppointmentSpeaker(null);
      toastSuccess("Appuntamento salvato nel calendario");
      await Promise.all([loadSchedule(), loadSpeakers()]);
    } catch (error) {
      toastError(error.response?.data?.message || error.message || "Appuntamento non salvato");
    } finally {
      setSaving(false);
    }
  };

  const openSpeakerHistory = async (speaker, outline = null) => {
    setHistoryModal({ speaker, outline, entries: [], stats: null, loading: true });
    try {
      const outlineQuery = outline?.id ? `?outline_id=${encodeURIComponent(outline.id)}` : "";
      const response = await api.get(
        `/discorsi-pubblici/areas/${selectedAreaId}/speakers/${speaker.id}/history${outlineQuery}`
      );
      setHistoryModal({
        speaker: response.data?.speaker || speaker,
        outline,
        entries: response.data?.entries || [],
        stats: response.data?.stats || null,
        loading: false,
      });
    } catch (error) {
      setHistoryModal(null);
      toastError(error.response?.data?.message || "Cronostoria non disponibile");
    }
  };

  const openOutlineSpeakers = async (outline) => {
    if (!selectedAreaId) {
      toastError("Importa prima un'area condivisa per consultare gli oratori");
      return;
    }
    setOutlineSpeakerSort("least_recent");
    setOutlineSpeakersModal({ outline, speakers: [], loading: true });
    try {
      const response = await api.get(
        `/discorsi-pubblici/areas/${selectedAreaId}/outlines/${outline.id}/speakers`
      );
      setOutlineSpeakersModal({
        outline: response.data?.outline || outline,
        speakers: response.data?.speakers || [],
        loading: false,
      });
    } catch (error) {
      setOutlineSpeakersModal(null);
      toastError(error.response?.data?.message || "Elenco oratori non disponibile");
    }
  };

  const openEditSpeaker = (speaker) => {
    setEditOutlineToAdd("");
    setEditSpeakerModal({
      speaker,
      form: {
        nome: speaker.nome || "",
        cognome: speaker.cognome || "",
        email: speaker.email || "",
        telefono: speaker.telefono || "",
        qualifica: speaker.qualifica || "anziano",
        outline_assignments: (speaker.outlines || []).map((outline) => ({
          outline_id: Number(outline.id),
          cantico_suggerito: outline.cantico_suggerito
            ? String(outline.cantico_suggerito)
            : "",
        })),
      },
    });
  };

  const updateEditSpeakerForm = (changes) => {
    setEditSpeakerModal((current) => current
      ? { ...current, form: { ...current.form, ...changes } }
      : current);
  };

  const addEditSpeakerOutline = () => {
    const outlineId = Number(editOutlineToAdd);
    if (!outlineId || !editSpeakerModal) return;
    if (editSpeakerModal.form.outline_assignments.some(
      (assignment) => Number(assignment.outline_id) === outlineId
    )) return;
    updateEditSpeakerForm({
      outline_assignments: [
        ...editSpeakerModal.form.outline_assignments,
        { outline_id: outlineId, cantico_suggerito: "" },
      ],
    });
    setEditOutlineToAdd("");
  };

  const saveSpeakerEdit = async (event) => {
    event.preventDefault();
    if (!editSpeakerModal?.form.outline_assignments.length) {
      toastError("Associa almeno uno schema all'oratore");
      return;
    }
    setSaving(true);
    try {
      const form = editSpeakerModal.form;
      await api.patch(
        `/discorsi-pubblici/areas/${selectedAreaId}/speakers/${editSpeakerModal.speaker.id}`,
        {
          nome: form.nome.trim(),
          cognome: form.cognome.trim(),
          email: form.email.trim() || null,
          telefono: form.telefono.trim() || null,
          qualifica: form.qualifica,
          outline_assignments: form.outline_assignments.map((assignment) => ({
            outline_id: Number(assignment.outline_id),
            cantico_suggerito: assignment.cantico_suggerito
              ? Number(assignment.cantico_suggerito)
              : null,
          })),
        }
      );
      setEditSpeakerModal(null);
      toastSuccess("Modifica proposta: attende l'approvazione incrociata");
      await loadRequests();
    } catch (error) {
      toastError(error.response?.data?.message || "Modifica non salvata");
    } finally {
      setSaving(false);
    }
  };

  const openCalendarEntry = (entry) => {
    setSelectedCalendarEntry(entry);
    setCalendarEntryEditing(false);
    setCalendarEntryForm({
      speaker_id: String(entry.speaker_id || ""),
      outline_id: String(entry.outline_id || ""),
      data: String(entry.data || "").slice(0, 10),
      ora_inizio: String(entry.ora_inizio || "").slice(0, 5),
      ora_fine: String(entry.ora_fine || "").slice(0, 5),
      cantico_suggerito: entry.cantico_suggerito ? String(entry.cantico_suggerito) : "",
      note: entry.note || "",
    });
  };

  const openCalendarNewAppointment = (data, direzione) => {
    setCalendarNewAppointmentOriginCongregationId("");
    setCalendarNewAppointment({
      speaker_id: "",
      direzione,
      congregazione_ospitante_id: "",
      outline_id: "",
      data,
      cantico_suggerito: "",
    });
  };

  const saveCalendarEntry = async () => {
    if (!selectedCalendarEntry || !calendarEntryForm) return;
    if (!calendarEntryForm.speaker_id || !calendarEntryForm.outline_id || !calendarEntryForm.data) {
      toastError("Seleziona oratore, schema e data");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/discorsi-pubblici/schedule/${selectedCalendarEntry.id}`, {
        speaker_id: Number(calendarEntryForm.speaker_id),
        outline_id: Number(calendarEntryForm.outline_id),
        data: calendarEntryForm.data,
        ora_inizio: calendarEntryForm.ora_inizio || undefined,
        ora_fine: calendarEntryForm.ora_fine || undefined,
        cantico_suggerito: calendarEntryForm.cantico_suggerito
          ? Number(calendarEntryForm.cantico_suggerito)
          : null,
        note: calendarEntryForm.note,
      });
      const refreshedEntries = await loadSchedule();
      const refreshedEntry = refreshedEntries.find(
        (entry) => Number(entry.id) === Number(selectedCalendarEntry.id)
      );
      if (refreshedEntry) openCalendarEntry(refreshedEntry);
      setCalendarEntryEditing(false);
      toastSuccess("Appuntamento aggiornato");
    } catch (error) {
      toastError(error.response?.data?.message || "Appuntamento non aggiornato");
    } finally {
      setSaving(false);
    }
  };

  const saveCoordinators = async () => {
    setSavingCoordinators(true);
    try {
      await api.put("/discorsi-pubblici/coordinators", { volontario_ids: coordinatorIds.map(Number) });
      setSavedCoordinatorIds([...coordinatorIds]);
      toastSuccess("Coordinatori aggiornati");
    } catch (error) {
      toastError(error.response?.data?.message || "Salvataggio non riuscito");
    } finally { setSavingCoordinators(false); }
  };

  const selectedCoordinators = useMemo(
    () => coordinatorIds.map((id) => coordinatorCandidates.find(
      (candidate) => Number(candidate.id) === Number(id)
    )).filter(Boolean),
    [coordinatorCandidates, coordinatorIds]
  );
  const availableCoordinatorCandidates = useMemo(
    () => coordinatorCandidates.filter(
      (candidate) => !coordinatorIds.includes(Number(candidate.id))
    ),
    [coordinatorCandidates, coordinatorIds]
  );
  const coordinatorsChanged =
    coordinatorIds.length !== savedCoordinatorIds.length ||
    coordinatorIds.some((id, index) => Number(id) !== Number(savedCoordinatorIds[index]));

  const addCoordinator = () => {
    const id = Number(coordinatorToAdd);
    if (!id || coordinatorIds.length >= 2 || coordinatorIds.includes(id)) return;
    setCoordinatorIds((current) => [...current, id]);
    setCoordinatorToAdd("");
  };

  const removeCoordinator = (id) => {
    setCoordinatorIds((current) => current.filter((item) => Number(item) !== Number(id)));
  };

  const loadSuggestions = async () => {
    if (scheduleForm.direzione !== "incoming") {
      toastError("Per uscite e discorsi locali scegli direttamente l'oratore compatibile");
      return;
    }
    if (!scheduleForm.outline_id || !scheduleForm.data) {
      toastError("Seleziona prima schema e data");
      return;
    }
    try {
      const response = await api.get("/discorsi-pubblici/suggestions", { params: {
        outline_id: scheduleForm.outline_id, data: scheduleForm.data,
      }});
      setSuggestions(response.data?.suggestions || []);
    } catch (error) {
      toastError(error.response?.data?.message || "Suggerimenti non disponibili");
    }
  };

  const cancelSchedule = async (entry) => {
    setSaving(true);
    try {
      await api.patch(`/discorsi-pubblici/schedule/${entry.id}`, { stato: "cancelled" });
      if (entry.invitation_sent_at) await api.post(`/discorsi-pubblici/schedule/${entry.id}/invite`, {});
      toastSuccess(entry.invitation_sent_at ? "Discorso annullato e cancellazione ICS inviata" : "Discorso annullato");
      await loadSchedule();
      return true;
    } catch (error) {
      toastError(error.response?.data?.message || "Annullamento non riuscito");
      return false;
    } finally { setSaving(false); }
  };

  const saveFeedback = async (entry) => {
    const draft = feedbackDrafts[entry.id] || { colore: entry.colore, note_consigli: entry.note_consigli || "" };
    if (!draft.colore) { toastError("Seleziona un colore"); return; }
    setSaving(true);
    try {
      await api.put(`/discorsi-pubblici/schedule/${entry.id}/feedback`, draft);
      toastSuccess("Feedback interno salvato");
      await Promise.all([loadSchedule(), loadSpeakers()]);
      if (historyModal) await openSpeakerHistory(historyModal.speaker);
    } catch (error) {
      toastError(error.response?.data?.message || "Feedback non salvato");
    } finally { setSaving(false); }
  };

  const sendInvite = async (entryId) => {
    setSaving(true);
    try {
      await api.post(`/discorsi-pubblici/schedule/${entryId}/invite`, {});
      toastSuccess("Invito email e ICS inviato");
      await loadSchedule();
    } catch (error) {
      toastError(error.response?.data?.message || "Invio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const review = async (requestId, decisione) => {
    setSaving(true);
    try {
      const response = await api.post(`/discorsi-pubblici/changes/${requestId}/review`, {
        decisione,
      });
      const affected = Number(response.data?.affected_assignments || 0);
      toastSuccess(
        decisione === "approved"
          ? response.data?.already_present
            ? "Richiesta approvata: l'oratore era già presente; area e schemi verificati"
            : affected > 0
            ? `Rimozione approvata: ${affected} incarich${affected === 1 ? "o da sostituire" : "i da sostituire"}`
            : "Modifica approvata"
          : "Modifica rifiutata"
      );
      await Promise.all([loadRequests(), loadBase()]);
    } catch (error) {
      toastError(error.response?.data?.message || "Revisione non riuscita");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[calc(100vh-7rem)] items-center justify-center px-5 py-10" role="status" aria-live="polite">
        <div className="w-full max-w-sm text-center">
          <div className="relative mx-auto flex h-20 w-20 items-center justify-center">
            <span className="absolute inset-0 rounded-2xl bg-primary-100 animate-ping motion-reduce:animate-none" aria-hidden="true" />
            <span className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-primary-100 bg-white p-2 shadow-sm">
              <img src={plannerIcon} alt="" className="h-full w-full object-contain" />
            </span>
          </div>
          <h1 className="mt-6 text-xl font-semibold text-gray-900">Discorsi Pubblici</h1>
          <p className="mt-2 text-sm leading-6 text-gray-600">Stiamo preparando calendario, oratori e schemi.</p>
          <div className="mx-auto mt-5 h-1.5 w-32 overflow-hidden rounded-full bg-primary-100" aria-hidden="true">
            <div className="h-full w-1/2 rounded-full bg-primary-600 animate-pulse motion-reduce:animate-none" />
          </div>
          <span className="sr-only">Caricamento in corso</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Discorsi Pubblici</h1>
        <p className="text-gray-600 mt-1">
          Coordina oratori, schemi e programma della congregazione {access?.congregation?.nome}.
          {outlines.length ? ` Catalogo ufficiale: ${outlines.length} schemi.` : ""}
        </p>
      </div>
      <nav aria-label="Sezioni Discorsi Pubblici" className="-mx-1 overflow-x-auto pb-1">
        <div className="flex min-w-max gap-1 rounded-xl border border-gray-200 bg-white p-1.5 shadow-sm">
        {tabs.map(([path, label, Icon]) => (
          <Link
            key={path}
            to={path}
            className={`inline-flex min-h-10 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              location.pathname === path
                ? "bg-primary-50 text-primary-700 shadow-sm ring-1 ring-primary-100"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            }`}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            {label}
          </Link>
        ))}
        </div>
      </nav>

      {section === "speakers" && (
        <>
          <form onSubmit={proposeSpeaker} className="overflow-hidden rounded-xl border bg-white shadow-sm">
            <div className="border-b border-primary-100 bg-gradient-to-r from-primary-50/80 to-white px-4 py-4 md:px-5">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <UserPlusIcon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <h2 className="font-semibold text-gray-900">Proponi un oratore {speakerDirection === "outgoing" ? "in uscita" : "in entrata"}</h2>
                  <p className="mt-0.5 text-sm text-gray-600">
                {speakerDirection === "incoming"
                  ? "La proposta diventa attiva dopo l'approvazione di un altro amministratore della stessa congregazione."
                  : "La proposta diventa attiva solo dopo l'approvazione di un amministratore di un'altra congregazione dell'area."}
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4 p-4 md:p-5">
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              {speakerDirection === "outgoing" && <select className="form-input" value={speakerForm.volontario_id} onChange={(e) => chooseLocalSpeaker(e.target.value)}>
                <option value="">Collega a proclamatore interno (facoltativo)</option>
                {localSpeakerCandidates.map((item) => <option key={item.id} value={item.id}>{item.cognome} {item.nome}</option>)}
              </select>}
              <select className="form-input" required disabled={speakerDirection === "outgoing"} value={speakerForm.congregazione_origine_id} onChange={(e) => setSpeakerForm({ ...speakerForm, volontario_id: "", congregazione_origine_id: e.target.value })}>
                <option value="">Congregazione di origine</option>
                {congregations.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
              </select>
              <input className="form-input" required placeholder="Nome" value={speakerForm.nome} onChange={(e) => setSpeakerForm({ ...speakerForm, nome: e.target.value })} />
              <input className="form-input" required placeholder="Cognome" value={speakerForm.cognome} onChange={(e) => setSpeakerForm({ ...speakerForm, cognome: e.target.value })} />
              <select className="form-input" value={speakerForm.qualifica} onChange={(e) => setSpeakerForm({ ...speakerForm, qualifica: e.target.value })}>
                <option value="anziano">Anziano</option><option value="servitore_ministero">Servitore di ministero</option>
              </select>
              <input type="email" className="form-input" placeholder="Email (facoltativa)" value={speakerForm.email} onChange={(e) => setSpeakerForm({ ...speakerForm, email: e.target.value })} />
              <input className="form-input" placeholder="Telefono (facoltativo)" value={speakerForm.telefono} onChange={(e) => setSpeakerForm({ ...speakerForm, telefono: e.target.value })} />
              <input className="form-input lg:col-span-2" required placeholder="Numeri schemi, es. 14, 65, 113" value={speakerForm.outline_numbers} onChange={(e) => setSpeakerForm({ ...speakerForm, outline_numbers: e.target.value })} />
            </div>
            <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
              <button disabled={saving || !selectedAreaId} className="btn-primary min-h-11 px-4">Invia proposta</button>
              <button type="button" onClick={downloadDirectoryTemplate} className="min-h-11 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Modello CSV</button>
              <button type="button" disabled={saving || !selectedAreaId} onClick={() => importFileRef.current?.click()} className="min-h-11 rounded-lg border border-primary-200 px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50">Importa CSV</button>
              <input ref={importFileRef} type="file" accept=".csv,text/csv" onChange={importSpeakersFile} className="hidden" />
            </div>
            </div>
          </form>
          <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
            <div className="border-b bg-gray-50/70 p-4 md:p-5">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
              <div className="xl:w-72 xl:flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-primary-600" />
                  <h2 className="font-semibold text-gray-900">{speakerDirection === "outgoing" ? "Oratori interni in uscita" : "Oratori ospiti in entrata"}</h2>
                </div>
                <p className="mt-1 text-sm leading-5 text-gray-500">Directory condivisa, visibile solo alle congregazioni che hanno importato l'area.</p>
              </div>
              <div className="grid w-full min-w-0 items-end gap-2 sm:grid-cols-2 xl:flex-1 xl:grid-cols-[minmax(18rem,1.8fr)_minmax(11rem,0.9fr)_minmax(10rem,0.8fr)_auto]">
                <PublicTalkSearchInput
                  label="Cerca oratori e schemi"
                  className="min-w-0 w-full xl:min-w-[18rem]"
                  value={speakerSearch}
                  onChange={(e) => setSpeakerSearch(e.target.value)}
                  placeholder="Cerca nome, cognome, congregazione, numero o titolo schema"
                  aria-label="Cerca oratori per nome, congregazione o schema"
                />
                <select className="form-input" value={speakerSort} onChange={(e) => setSpeakerSort(e.target.value)} aria-label="Ordina oratori">
                  <option value="default">Ordine predefinito</option>
                  <option value="alphabetical">Ordine alfabetico (cognome)</option>
                  <option value="never_first">Prima chi non è mai intervenuto</option>
                  <option value="oldest_first">Dal meno recente</option>
                  <option value="recent_first">Dal più recente</option>
                </select>
                <select className="form-input" value={selectedAreaId} onChange={(e) => setSelectedAreaId(e.target.value)}>
                  <option value="">Seleziona area</option>
                  {areas.map((area) => <option key={area.id} value={area.id}>{area.nome} ({area.speakers_count})</option>)}
                </select>
                <button type="button" onClick={exportSpeakers} disabled={!selectedAreaId} className="min-h-11 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-white">Esporta CSV</button>
              </div>
              </div>
            </div>
            {visibleSpeakers.length ? (
              <div className="divide-y">
                {visibleSpeakers.map((speaker) => (
                  <div key={speaker.id} className="grid gap-4 p-4 transition-colors hover:bg-gray-50/70 lg:grid-cols-[minmax(11rem,1fr)_minmax(12rem,1fr)_minmax(14rem,1.35fr)_12rem] lg:items-start lg:p-5">
                    <div><div className="font-semibold text-gray-900">{speaker.nome} {speaker.cognome}</div><div className="mt-1 text-xs text-gray-500">{speaker.qualifica === "anziano" ? "Anziano" : "Servitore di ministero"}{speaker.volontario_id ? " · utente collegato" : " · directory"}</div></div>
                    <div className="text-sm text-gray-700"><div>{speaker.congregazione_origine}</div><div className="text-xs text-gray-500">{speaker.email || "Email non indicata"}{speaker.telefono ? ` · ${speaker.telefono}` : ""}</div></div>
                    <div>
                      <div className="text-sm text-gray-600">Schemi: {speaker.outlines.map((outline) => outline.numero).join(", ") || "nessuno"}</div>
                      <div className="mt-1 text-xs text-gray-500">
                        {Number(speaker.history_count || 0) === 0
                          ? "Mai intervenuto per questa congregazione"
                          : `Ultimo discorso: ${formatDate(speaker.ultima_attivita)} · ${speaker.history_count} in cronostoria`}
                      </div>
                      {speaker.feedback_count > 0 && <div className="mt-2 flex flex-wrap items-center gap-2 text-xs"><span className={`rounded-full px-2.5 py-1 font-semibold ${feedbackFromAverage(speaker.feedback_medio)?.className}`}>Gradimento: {feedbackFromAverage(speaker.feedback_medio)?.label}</span><span className="text-gray-500">{speaker.feedback_count} feedback locali</span></div>}
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 lg:w-48 lg:justify-self-end">
                      <button type="button" disabled={saving} onClick={() => openAppointment(speaker)} className="col-span-2 min-h-10 rounded-md bg-primary-50 px-2.5 py-1.5 text-sm font-semibold text-primary-700 hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50">Appuntamento</button>
                      <button type="button" disabled={saving} onClick={() => openSpeakerHistory(speaker)} className="min-h-10 rounded-md px-2 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100">Cronostoria</button>
                      <button type="button" disabled={saving} onClick={() => openEditSpeaker(speaker)} className="min-h-10 rounded-md px-2 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50">Modifica</button>
                      {access?.isAdmin && Number(speaker.congregazione_origine_id) === Number(access?.congregation?.id) && (
                        <button type="button" disabled={saving} onClick={() => removeSpeaker(speaker)} className="col-span-2 min-h-10 rounded-md px-2 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50">Rimuovi</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="p-8 text-center text-gray-500">Nessun oratore corrispondente.</div>}
          </div>
        </>
      )}

      {section === "settings" && (
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Impostazioni Discorsi Pubblici</h2>
            <p className="mt-1 text-sm text-gray-600">Gestisci i coordinatori e le aree condivise della congregazione attiva.</p>
          </div>
          {access?.isAdmin && (
            <section className="bg-white border rounded-xl p-4 md:p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold text-gray-900">Coordinatori Discorsi Pubblici</h2>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{coordinatorIds.length}/2 assegnati</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Assegna fino a due utenti della congregazione attiva.</p>
                </div>
                {coordinatorsChanged ? (
                  <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">
                    Modifiche da salvare
                  </span>
                ) : coordinatorIds.length ? (
                  <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-800">
                    <CheckCircleIcon className="h-4 w-4" /> Configurazione salvata
                  </span>
                ) : (
                  <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                    Nessun coordinatore assegnato
                  </span>
                )}
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {[0, 1].map((slot) => {
                  const coordinator = selectedCoordinators[slot];
                  return (
                    <div key={slot} className={`min-h-24 rounded-lg border p-3 ${coordinator ? "border-primary-200 bg-primary-50/40" : "border-dashed border-gray-300 bg-gray-50"}`}>
                      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Coordinatore {slot + 1}</div>
                      {coordinator ? (
                        <div className="mt-2 flex items-center gap-2">
                          <UserCircleIcon className="h-8 w-8 flex-shrink-0 text-primary-600" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium text-gray-900">{coordinator.nome} {coordinator.cognome}</div>
                            {coordinator.email && <div className="truncate text-xs text-gray-500">{coordinator.email}</div>}
                          </div>
                          <button type="button" onClick={() => removeCoordinator(coordinator.id)} className="rounded-md p-1.5 text-gray-500 hover:bg-white hover:text-red-600" title={`Rimuovi ${coordinator.nome} ${coordinator.cognome}`} aria-label={`Rimuovi ${coordinator.nome} ${coordinator.cognome}`}>
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      ) : (
                        <div className="mt-3 text-sm text-gray-500">Posto disponibile</div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-end">
                <label className="flex-1 text-sm font-medium text-gray-700">
                  Aggiungi coordinatore
                  <select className="form-input mt-1" value={coordinatorToAdd} disabled={coordinatorIds.length >= 2 || savingCoordinators}
                    onChange={(event) => setCoordinatorToAdd(event.target.value)}>
                    <option value="">{coordinatorIds.length >= 2 ? "Hai già assegnato due coordinatori" : "Seleziona un utente"}</option>
                    {availableCoordinatorCandidates.map((item) => <option key={item.id} value={item.id}>{item.nome} {item.cognome}</option>)}
                  </select>
                </label>
                <button type="button" disabled={!coordinatorToAdd || coordinatorIds.length >= 2 || savingCoordinators} onClick={addCoordinator} className="btn-secondary px-4">
                  <UserPlusIcon className="mr-2 inline h-5 w-5" />Aggiungi
                </button>
                <button type="button" disabled={!coordinatorsChanged || savingCoordinators} onClick={saveCoordinators} className="btn-primary px-4">
                  {savingCoordinators ? "Salvataggio..." : coordinatorsChanged ? "Salva modifiche" : "Salvato"}
                </button>
              </div>
            </section>
          )}
          <div className="grid gap-4 lg:grid-cols-2">
            <form onSubmit={importArea} className="bg-white border rounded-xl p-4">
              <h2 className="font-semibold text-gray-900">Importa un'area</h2>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <input className="form-input flex-1" value={importCode} onChange={(e) => setImportCode(e.target.value)} placeholder="Codice di condivisione" />
                <button disabled={saving} className="btn-primary px-4">Importa</button>
              </div>
            </form>
            <form onSubmit={createArea} className="bg-white border rounded-xl p-4">
              <h2 className="font-semibold text-gray-900">Proponi una nuova area</h2>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <input className="form-input flex-1" value={areaName} onChange={(e) => setAreaName(e.target.value)} placeholder="Nome area" />
                <button disabled={saving} className="btn-primary px-4">Proponi</button>
              </div>
            </form>
          </div>
        </section>
      )}

      {section === "outlines" && (
        <section className="overflow-hidden rounded-xl border bg-white shadow-sm">
          <div className="border-b border-primary-100 bg-gradient-to-r from-primary-50/80 to-white px-4 py-4 md:px-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <BookOpenIcon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <h2 className="font-semibold text-gray-900">Catalogo degli schemi</h2>
                  <p className="mt-0.5 text-sm leading-5 text-gray-600">Elenco ufficiale usato per associare gli oratori e pianificare i discorsi.</p>
                </div>
              </div>
              <span className="inline-flex w-fit shrink-0 items-center rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-primary-700 ring-1 ring-primary-100">
                {outlines.length} schemi attivi
              </span>
            </div>
          </div>
          <div className="border-b bg-gray-50/70 p-4 md:p-5">
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(15rem,22rem)]">
            <PublicTalkSearchInput
              label="Cerca nel catalogo schemi"
              value={outlineSearch}
              onChange={(event) => setOutlineSearch(event.target.value)}
              placeholder="Cerca per numero, titolo o fonte"
              aria-label="Cerca nel catalogo degli schemi"
            />
            <label className="block text-sm font-medium text-gray-700">
              Area condivisa
              <select className="form-input mt-1 w-full" aria-label="Area condivisa del catalogo" value={selectedAreaId} onChange={(event) => setSelectedAreaId(event.target.value)} disabled={!areas.length}>
                {areas.length ? areas.map((area) => <option key={area.id} value={area.id}>{area.nome}</option>) : <option value="">Nessuna area importata</option>}
              </select>
            </label>
            </div>
            <p className="mt-2 text-xs leading-5 text-gray-500">Seleziona uno schema per vedere gli oratori che lo hanno nel repertorio e la loro ultima esecuzione presso {access?.congregation?.nome || "la congregazione attiva"}. Il catalogo resta in sola lettura per proteggere assegnazioni e storico.</p>
          </div>
          {visibleOutlines.length ? (
            <ul className="divide-y" aria-label="Elenco degli schemi">
              {visibleOutlines.map((outline) => (
                <li key={outline.id}>
                  <button type="button" onClick={() => openOutlineSpeakers(outline)} className="flex w-full gap-3 p-4 text-left transition-colors hover:bg-primary-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-600 md:items-center md:px-5" aria-label={`Vedi gli oratori dello schema ${outline.numero}: ${outline.titolo}`}>
                    <span className="flex h-10 min-w-10 items-center justify-center rounded-lg bg-primary-50 px-2 text-sm font-bold text-primary-700" aria-hidden="true">
                      {outline.numero}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900">{outline.titolo}</div>
                      {outline.fonte && <div className="mt-1 text-xs text-gray-500">Fonte: {outline.fonte}</div>}
                    </div>
                    <span className="hidden shrink-0 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 sm:inline-flex">Attivo</span>
                    <span className="shrink-0 text-sm font-medium text-primary-700">Oratori</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-8 text-center text-gray-500">Nessuno schema corrispondente alla ricerca.</div>
          )}
        </section>
      )}

      {section === "calendar" && (
        <>
          <form onSubmit={createSchedule} className="bg-white border rounded-xl p-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <PublicTalkAppointmentTimes form={scheduleForm} onChange={patch => setScheduleForm(current => ({ ...current, ...patch }))} />
            <select className="form-input" value={scheduleForm.direzione} onChange={(e) => {
              setScheduleOriginCongregationId("");
              setScheduleForm({ ...scheduleForm, direzione: e.target.value, speaker_id: "", outline_id: "", congregazione_ospitante_id: "", cantico_suggerito: "" });
            }}>
              <option value="incoming">Oratore in entrata</option>
              <option value="outgoing">Oratore in uscita</option>
              <option value="local">Discorso locale</option>
            </select>
            <select className="form-input" value={selectedAreaId} onChange={(e) => {
              setSelectedAreaId(e.target.value);
              setScheduleOriginCongregationId("");
              setScheduleForm((current) => ({ ...current, speaker_id: "", outline_id: "", cantico_suggerito: "" }));
            }} required>
              <option value="">Area</option>
              {areas.filter((area) => area.stato === "active").map((area) => <option key={area.id} value={area.id}>{area.nome}</option>)}
            </select>
            {scheduleForm.direzione === "incoming" && (
              <select
                aria-label="Congregazione di provenienza"
                className="form-input"
                value={scheduleOriginCongregationId}
                onChange={(e) => {
                  setScheduleOriginCongregationId(e.target.value);
                  setScheduleForm((current) => ({ ...current, speaker_id: "", outline_id: "", cantico_suggerito: "" }));
                }}
                required
              >
                <option value="">Congregazione di provenienza</option>
                {incomingOriginCongregations.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
              </select>
            )}
            <select
              aria-label="Oratore del calendario"
              className="form-input disabled:cursor-not-allowed disabled:bg-gray-100"
              disabled={scheduleForm.direzione === "incoming" && !scheduleOriginCongregationId}
              value={scheduleForm.speaker_id}
              onChange={(e) => setScheduleForm({ ...scheduleForm, speaker_id: e.target.value, outline_id: "", cantico_suggerito: "" })}
              required
            >
              <option value="">{scheduleForm.direzione === "incoming" && !scheduleOriginCongregationId ? "Prima seleziona la congregazione" : scheduleForm.direzione === "outgoing" ? "Oratore interno" : scheduleForm.direzione === "local" ? "Oratore locale" : "Oratore ospite"}</option>
              {eligibleSpeakers.map((speaker) => <option key={speaker.id} value={speaker.id}>{speaker.cognome} {speaker.nome} - {speaker.congregazione_origine}</option>)}
            </select>
            <select className="form-input disabled:cursor-not-allowed disabled:bg-gray-100" disabled={!scheduleForm.speaker_id} value={scheduleForm.outline_id} onChange={(e) => setScheduleForm({ ...scheduleForm, outline_id: e.target.value, cantico_suggerito: String(scheduleOutlineOptions.find((outline) => String(outline.id) === e.target.value)?.cantico_suggerito || "") })} required>
              <option value="">{selectedScheduleSpeaker ? "Schema dell'oratore" : "Schema"}</option>
              {selectedScheduleSpeaker && <optgroup label="Schemi abilitati per l’oratore">
                {scheduleOutlineOptions.map((outline) => <option key={outline.id} value={outline.id}>{outline.numero}. {outline.titolo}</option>)}
              </optgroup>}
              {selectedScheduleSpeaker && scheduleOtherOutlineOptions.length > 0 && <optgroup label="Altro schema dal catalogo ufficiale">
                {scheduleOtherOutlineOptions.map((outline) => <option key={outline.id} value={outline.id}>{outline.numero}. {outline.titolo}</option>)}
              </optgroup>}
            </select>
            <input type="date" className="form-input" value={scheduleForm.data} onChange={(e) => setScheduleForm({ ...scheduleForm, data: e.target.value })} required />
            {scheduleForm.direzione === "outgoing" && <select className="form-input" required value={scheduleForm.congregazione_ospitante_id} onChange={(e) => setScheduleForm({ ...scheduleForm, congregazione_ospitante_id: e.target.value })}>
              <option value="">Congregazione di destinazione</option>
              {sharedDestinationCongregations.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
            </select>}
            <input type="number" min="1" inputMode="numeric" className="form-input" value={scheduleForm.cantico_suggerito} onChange={(e) => setScheduleForm({ ...scheduleForm, cantico_suggerito: e.target.value })} placeholder="Cantico suggerito (facoltativo)" />
            <div className="flex gap-2">
              {scheduleForm.direzione === "incoming" && <button type="button" onClick={loadSuggestions} className="px-3 py-2 border border-primary-200 text-primary-700 rounded-lg">Suggerisci</button>}
              <button disabled={saving} className="btn-primary px-4 flex-1">Aggiungi</button>
            </div>
          </form>
          {scheduleForm.direzione === "incoming" && suggestions.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h2 className="font-semibold text-blue-950">Suggerimenti intelligenti</h2>
              <p className="text-sm text-blue-800 mb-3">Prima chi non è mai stato ascoltato qui, poi chi manca da più tempo e chi ha trattato meno di recente lo schema scelto.</p>
              {suggestions[0]?.ultima_ricorrenza_schema && <p className="mb-3 border-l-2 border-primary-500 pl-3 text-sm text-primary-900">Lo schema selezionato risulta già trattato a Palermo Uditore nel mese di {formatMonth(new Date(`${String(suggestions[0].ultima_ricorrenza_schema).slice(0, 10)}T12:00:00`))}.</p>}
              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {suggestions.slice(0, 9).map((speaker, index) => (
                  <button type="button" key={speaker.id} onClick={() => setScheduleForm({ ...scheduleForm, speaker_id: String(speaker.id) })}
                    className="text-left bg-white border rounded-lg p-3 hover:border-primary-400">
                    <div className="font-medium">{index + 1}. {speaker.nome} {speaker.cognome}</div>
                    <div className="text-xs text-gray-600">{speaker.congregazione_origine} · ultima visita: {speaker.ultima_visita ? formatDate(speaker.ultima_visita) : "mai"}</div>
                    {speaker.feedback_count > 0 && <div className="text-xs text-gray-500">Feedback locale {Number(speaker.feedback_medio).toFixed(1)}/3 ({speaker.feedback_count})</div>}
                  </button>
                ))}
              </div>
            </div>
          )}

          <section className="bg-white border rounded-xl overflow-hidden">
            <div className="p-4 md:p-5 border-b">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="h-5 w-5 text-primary-600" />
                    <h2 className="font-semibold text-gray-900">Calendario discorsi</h2>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">La griglia mostra solo le adunanze del fine settimana. Trimestre e semestre partono dal mese selezionato.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {calendarFilterOptions.map(([value, label]) => (
                    <button type="button" key={value} onClick={() => {
                      setCalendarFilter(value);
                      if (value !== "specific") setCalendarMonthKey(monthKeyFromDate(new Date()));
                      if (value === "specific" && !calendarSpecificDate) {
                        const today = new Date();
                        setCalendarSpecificDate(dateKeyFromDate(today));
                        setCalendarMonthKey(monthKeyFromDate(today));
                      }
                    }} className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${calendarFilter === value ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                {calendarFilter === "specific" ? (
                  <label className="text-sm font-medium text-gray-700">
                    Mostra la data
                    <input type="date" className="form-input mt-1" value={calendarSpecificDate} onChange={(event) => {
                      const value = event.target.value;
                      setCalendarSpecificDate(value);
                      if (value) setCalendarMonthKey(value.slice(0, 7));
                    }} />
                  </label>
                ) : (
                  <div className="text-sm text-gray-600">
                    Programma: <span className="font-medium text-gray-900">{calendarRange.label}</span>
                    <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs">{filteredScheduleEntries.length} {filteredScheduleEntries.length === 1 ? "discorso" : "discorsi"}</span>
                  </div>
                )}

                <div className="flex items-center justify-between gap-2 rounded-lg border bg-gray-50 p-1 sm:justify-end">
                  <button type="button" onClick={() => moveCalendarPeriod(-1)} className="rounded-md p-2 text-gray-600 hover:bg-white hover:text-primary-700" aria-label="Periodo precedente" title="Periodo precedente">
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <div className="min-w-[10rem] text-center text-sm font-semibold capitalize text-gray-900">{calendarFilter === "month" || calendarFilter === "specific" ? formatMonth(calendarMonthDate) : calendarRange.label}</div>
                  <button type="button" onClick={() => moveCalendarPeriod(1)} className="rounded-md p-2 text-gray-600 hover:bg-white hover:text-primary-700" aria-label="Periodo successivo" title="Periodo successivo">
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                  <button type="button" onClick={showCurrentPeriod} className="rounded-md px-2.5 py-2 text-xs font-medium text-primary-700 hover:bg-white">Oggi</button>
                </div>
              </div>
            </div>

            <div className="space-y-6 p-3 sm:p-4">
              {calendarMonths.map((month) => (
                <div key={monthKeyFromDate(month)} className="overflow-hidden rounded-xl border">
                  {calendarMonths.length > 1 && <h3 className="border-b bg-gray-50 px-3 py-2 text-sm font-semibold capitalize text-gray-800">{formatMonth(month)}</h3>}
                  <div className="divide-y">
                    {getWeekendDaysForMonth(month).filter((day) =>
                      !day.empty && day.key >= calendarRange.start && day.key <= calendarRange.end
                    ).map((day) => {
                      const groups = calendarEntriesByDate.get(day.key) || { incoming: [], outgoing: [], local: [] };
                      const inSelectedRange = day.key >= calendarRange.start && day.key <= calendarRange.end;
                      const DayList = ({ direction, title }) => (
                        <div className={`rounded-lg border p-3 ${directionMeta[direction].className}`}>
                          <div className="mb-2 text-xs font-semibold uppercase tracking-wide">{title}</div>
                          <div className="space-y-2">
                            {groups[direction].length ? groups[direction].map((entry) => (
                              <button type="button" key={entry.id} onClick={() => openCalendarEntry(entry)} aria-label={`Gestisci il discorso di ${entry.nome} ${entry.cognome}`} className={`min-h-11 w-full rounded-md border px-2 py-2 text-left text-sm font-medium leading-snug transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${entry.stato === "cancelled" ? "border-gray-200 bg-white/70 text-gray-500 line-through" : entry.stato === "declined" ? "border-red-300 bg-red-50 text-red-800 hover:bg-red-100" : "border-current/15 bg-white/70 hover:bg-white"}`}>
                                <span className="flex items-start justify-between gap-2">
                                  <span><span className="mr-1 whitespace-nowrap">{String(entry.ora_inizio || "").slice(0, 5) || "—"}</span>{entry.nome} {entry.cognome}</span>
                                  <span className="shrink-0 text-xs font-semibold text-current/70">Gestisci</span>
                                </span>
                                {entry.stato === "declined" && <span className="mt-1 block text-xs font-semibold uppercase">Da sostituire</span>}
                              </button>
                            )) : direction === "local" ? (
                              <div className="py-2 text-sm opacity-70">Nessun incarico</div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => openCalendarNewAppointment(day.key, direction)}
                                aria-label={`Aggiungi un oratore ${direction === "incoming" ? "in entrata" : "in uscita"} per ${formatDate(day.key)}`}
                                className="flex min-h-11 w-full items-center justify-center rounded-md border border-dashed border-current/35 bg-white/60 px-3 py-2 text-sm font-semibold transition-colors hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                              >
                                + Aggiungi incarico
                              </button>
                            )}
                          </div>
                        </div>
                      );
                      return (
                        <div key={day.key} className={`p-3 sm:p-4 ${inSelectedRange ? "bg-primary-50/20" : "bg-white"}`}>
                          <div className="mb-3 flex items-center gap-2">
                            <span className={`flex h-8 min-w-8 items-center justify-center rounded-full text-sm font-semibold ${day.key === dateKeyFromDate(new Date()) ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-800"}`}>{day.day}</span>
                            <h4 className="font-semibold capitalize text-gray-900">{day.weekday === 6 ? "Sabato" : "Domenica"} {formatDate(day.key).replace(/^[^,]+,\s*/, "")}</h4>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <DayList direction="outgoing" title="Oratori in uscita" />
                            <DayList direction="incoming" title="Oratori in entrata" />
                          </div>
                          {groups.local.length > 0 && <div className="mt-3"><DayList direction="local" title="Discorsi locali" /></div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="space-y-3">
            {filteredScheduleEntries.map((entry) => (
              <div key={entry.id} className="bg-white border rounded-xl p-4 space-y-4">
                <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
                  <div>
                    <div className="font-semibold">{formatDate(entry.data)} · {String(entry.ora_inizio || "").slice(0, 5)}</div>
                    <div>{entry.nome} {entry.cognome} · {entry.direzione === "outgoing" ? `destinazione: ${entry.congregazione_ospitante}` : entry.congregazione_origine}</div>
                    <div className="text-sm text-gray-600">Schema {entry.numero}: {entry.titolo}</div>
                    {entry.cantico_suggerito && <div className="text-sm text-gray-600">Cantico suggerito: {entry.cantico_suggerito}</div>}
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`text-xs uppercase rounded-full border px-3 py-1 ${directionMeta[entry.direzione || "incoming"].className}`}>{directionMeta[entry.direzione || "incoming"].label}</span>
                    <span className={`text-xs uppercase rounded-full px-3 py-1 ${entry.stato === "declined" ? "bg-red-100 text-red-800" : "bg-gray-100"}`}>{entry.stato === "declined" ? "Da sostituire" : entry.stato}</span>
                    {!['cancelled', 'declined'].includes(entry.stato) && <button disabled={saving} onClick={() => sendInvite(entry.id)} className="text-sm text-primary-700 font-semibold">{entry.invitation_sent_at ? "Invia aggiornamento ICS" : "Invia email + ICS"}</button>}
                    {!['cancelled', 'declined'].includes(entry.stato) && <button disabled={saving} onClick={() => cancelSchedule(entry)} className="text-sm text-red-700 font-semibold">Annulla</button>}
                  </div>
                </div>
                {entry.can_manage_feedback && new Date(`${String(entry.data).slice(0, 10)}T${String(entry.ora_fine || "23:59").slice(0, 5)}:00`) <= new Date() && !['cancelled', 'declined'].includes(entry.stato) && (
                  <div className="border-t pt-4">
                    <div className="font-medium text-sm mb-2">Feedback interno — visibile solo ad admin e coordinatori locali</div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {[3, 2, 1].map((color) => {
                        const activeColor = (feedbackDrafts[entry.id]?.colore || entry.colore) === color;
                        const meta = feedbackMeta[color];
                        return <button type="button" key={color} onClick={() => setFeedbackDrafts({ ...feedbackDrafts, [entry.id]: { colore: color, note_consigli: feedbackDrafts[entry.id]?.note_consigli ?? entry.note_consigli ?? "" } })}
                          className={`px-3 py-1 rounded-full text-sm ${meta.className} ${activeColor ? "ring-2 ring-offset-1 ring-gray-700" : ""}`}>{meta.label}</button>;
                      })}
                    </div>
                    <div className="flex flex-col md:flex-row gap-2">
                      <textarea className="form-input min-h-20 flex-1" placeholder="Note e consigli" value={feedbackDrafts[entry.id]?.note_consigli ?? entry.note_consigli ?? ""}
                        onChange={(e) => setFeedbackDrafts({ ...feedbackDrafts, [entry.id]: { colore: feedbackDrafts[entry.id]?.colore || entry.colore || 3, note_consigli: e.target.value } })} />
                      <button type="button" disabled={saving} onClick={() => saveFeedback(entry)} className="btn-primary px-4 self-end">Salva feedback</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {!filteredScheduleEntries.length && <div className="bg-white border rounded-xl p-8 text-center text-gray-500">Nessun discorso programmato nel periodo selezionato.</div>}
          </div>
        </>
      )}

      {calendarNewAppointment && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40 p-0 sm:items-center sm:justify-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="calendar-new-appointment-title" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !saving) setCalendarNewAppointment(null);
        }}>
          <form onSubmit={(event) => createSchedule(event, { closeCalendarModal: true, form: calendarNewAppointment })} className="flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:max-w-2xl sm:rounded-2xl">
            <div className="flex shrink-0 items-start justify-between gap-4 border-b p-5">
              <div>
                <p className="text-sm font-medium text-primary-700">Nuovo appuntamento dal calendario</p>
                <h2 id="calendar-new-appointment-title" className="mt-1 text-xl font-semibold text-gray-900">{calendarNewAppointment.direzione === "incoming" ? "Oratore in entrata" : "Oratore in uscita"}</h2>
                <p className="mt-1 text-sm text-gray-600">{formatDate(calendarNewAppointment.data)} · la data è già impostata sul fine settimana selezionato.</p>
              </div>
              <button type="button" disabled={saving} onClick={() => setCalendarNewAppointment(null)} className="min-h-11 min-w-11 rounded-lg p-2 text-gray-500 hover:bg-gray-100" aria-label="Chiudi nuovo appuntamento">
                <XMarkIcon className="mx-auto h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <PublicTalkAppointmentTimes form={calendarNewAppointment} onChange={patch => setCalendarNewAppointment(current => ({ ...current, ...patch }))} />
                <label className="text-sm font-medium text-gray-700 sm:col-span-2">Area condivisa
                  <select required aria-label="Area condivisa" className="form-input mt-1" value={selectedAreaId} onChange={(event) => {
                    setSelectedAreaId(event.target.value);
                    setCalendarNewAppointmentOriginCongregationId("");
                    setCalendarNewAppointment((current) => ({ ...current, speaker_id: "", outline_id: "", congregazione_ospitante_id: "", cantico_suggerito: "" }));
                  }}>
                    <option value="">Seleziona un’area</option>
                    {areas.filter((area) => area.stato === "active").map((area) => <option key={area.id} value={area.id}>{area.nome}</option>)}
                  </select>
                </label>
                {calendarNewAppointment.direzione === "incoming" && (
                  <label className="text-sm font-medium text-gray-700 sm:col-span-2">Congregazione di provenienza
                    <select
                      required
                      aria-label="Congregazione di provenienza"
                      className="form-input mt-1"
                      value={calendarNewAppointmentOriginCongregationId}
                      onChange={(event) => {
                        setCalendarNewAppointmentOriginCongregationId(event.target.value);
                        setCalendarNewAppointment((current) => ({ ...current, speaker_id: "", outline_id: "", cantico_suggerito: "" }));
                      }}
                    >
                      <option value="">Seleziona la congregazione</option>
                      {incomingOriginCongregations.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
                    </select>
                    <span className="mt-1 block text-xs font-normal text-gray-500">Sono elencate solo le congregazioni collegate all’area condivisa selezionata.</span>
                  </label>
                )}
                <label className="text-sm font-medium text-gray-700 sm:col-span-2">Oratore
                  <select
                    required
                    aria-label="Oratore"
                    disabled={calendarNewAppointment.direzione === "incoming" && !calendarNewAppointmentOriginCongregationId}
                    className="form-input mt-1 disabled:cursor-not-allowed disabled:bg-gray-100"
                    value={calendarNewAppointment.speaker_id}
                    onChange={(event) => setCalendarNewAppointment((current) => ({ ...current, speaker_id: event.target.value, outline_id: "", cantico_suggerito: "" }))}
                  >
                    <option value="">{calendarNewAppointment.direzione === "incoming" && !calendarNewAppointmentOriginCongregationId ? "Prima seleziona la congregazione" : calendarNewAppointment.direzione === "incoming" ? "Seleziona un oratore ospite" : "Seleziona un oratore interno"}</option>
                    {calendarNewAppointmentEligibleSpeakers.map((speaker) => <option key={speaker.id} value={speaker.id}>{speaker.cognome} {speaker.nome} · {speaker.congregazione_origine}</option>)}
                  </select>
                  <span className="mt-1 block text-xs font-normal text-gray-500">{calendarNewAppointment.direzione === "incoming" ? "Dopo la congregazione, sono mostrati solo i suoi oratori abilitati nell’area." : "Sono mostrati gli oratori della congregazione attiva abilitati nell’area."}</span>
                </label>
                <label className="text-sm font-medium text-gray-700 sm:col-span-2">Schema
                  <select required aria-label="Schema" disabled={!calendarNewAppointment.speaker_id} className="form-input mt-1 disabled:cursor-not-allowed disabled:bg-gray-100" value={calendarNewAppointment.outline_id} onChange={(event) => {
                    const outline = calendarNewAppointmentOutlineOptions.find((item) => String(item.id) === event.target.value);
                    setCalendarNewAppointment((current) => ({ ...current, outline_id: event.target.value, cantico_suggerito: outline?.cantico_suggerito ? String(outline.cantico_suggerito) : "" }));
                  }}>
                    <option value="">{calendarNewAppointmentSelectedSpeaker ? "Seleziona uno schema" : "Prima seleziona l’oratore"}</option>
                    {calendarNewAppointmentSelectedSpeaker && <optgroup label="Schemi abilitati per l’oratore">
                      {calendarNewAppointmentOutlineOptions.map((outline) => <option key={outline.id} value={outline.id}>{outline.numero}. {outline.titolo}</option>)}
                    </optgroup>}
                    {calendarNewAppointmentSelectedSpeaker && calendarNewAppointmentOtherOutlineOptions.length > 0 && <optgroup label="Altro schema dal catalogo ufficiale">
                      {calendarNewAppointmentOtherOutlineOptions.map((outline) => <option key={outline.id} value={outline.id}>{outline.numero}. {outline.titolo}</option>)}
                    </optgroup>}
                  </select>
                  <span className="mt-1 block text-xs font-normal text-gray-500">Prima trovi gli schemi abituali dell’oratore; “Altro schema” permette una scelta eccezionale dal catalogo ufficiale senza modificare la sua rosa.</span>
                </label>
                {calendarNewAppointment.direzione === "outgoing" ? (
                  <label className="text-sm font-medium text-gray-700 sm:col-span-2">Congregazione di destinazione
                    <select required aria-label="Congregazione di destinazione" className="form-input mt-1" value={calendarNewAppointment.congregazione_ospitante_id} onChange={(event) => setCalendarNewAppointment((current) => ({ ...current, congregazione_ospitante_id: event.target.value }))}>
                      <option value="">Seleziona la destinazione</option>
                      {sharedDestinationCongregations.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
                    </select>
                    <span className="mt-1 block text-xs font-normal text-gray-500">Sono disponibili solo congregazioni collegate alla stessa area.</span>
                  </label>
                ) : (
                  <div className="rounded-xl border bg-gray-50 p-3 text-sm sm:col-span-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Congregazione ospitante</div>
                    <div className="mt-1 font-medium text-gray-900">{access?.congregation?.nome}</div>
                  </div>
                )}
                <label className="text-sm font-medium text-gray-700">Data del discorso
                  <input required readOnly type="date" className="form-input mt-1 bg-gray-50" value={calendarNewAppointment.data} aria-label="Data del discorso" />
                </label>
                <label className="text-sm font-medium text-gray-700">Cantico suggerito
                  <input type="number" min="1" inputMode="numeric" className="form-input mt-1" value={calendarNewAppointment.cantico_suggerito} onChange={(event) => setCalendarNewAppointment((current) => ({ ...current, cantico_suggerito: event.target.value }))} placeholder="Facoltativo" />
                </label>
              </div>
            </div>
            <div className="flex shrink-0 flex-col-reverse gap-2 border-t bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end">
              <button type="button" disabled={saving} onClick={() => setCalendarNewAppointment(null)} className="min-h-11 rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50">Annulla</button>
              <button disabled={saving} className="btn-primary min-h-11 px-5">{saving ? "Salvataggio..." : "Aggiungi appuntamento"}</button>
            </div>
          </form>
        </div>
      )}

      {appointmentSpeaker && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40 p-0 sm:items-center sm:justify-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="speaker-appointment-title" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !saving) setAppointmentSpeaker(null);
        }}>
          <form onSubmit={createSpeakerAppointment} className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:max-w-2xl sm:rounded-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-primary-700">Nuovo appuntamento · {speakerDirection === "incoming" ? "oratore in entrata" : "oratore in uscita"}</p>
                <h2 id="speaker-appointment-title" className="mt-1 text-xl font-semibold text-gray-900">{appointmentSpeaker.nome} {appointmentSpeaker.cognome}</h2>
                <p className="mt-1 text-sm text-gray-600">Provenienza: {appointmentSpeaker.congregazione_origine}</p>
              </div>
              <button type="button" disabled={saving} onClick={() => setAppointmentSpeaker(null)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100" aria-label="Chiudi appuntamento">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <PublicTalkAppointmentTimes form={appointmentForm} onChange={patch => setAppointmentForm(current => ({ ...current, ...patch }))} />
              <label className="text-sm font-medium text-gray-700">
                Data del discorso
                <input type="date" className="form-input mt-1" required value={appointmentForm.data} onChange={(event) => setAppointmentForm({ ...appointmentForm, data: event.target.value })} />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Schema
                <select className="form-input mt-1" required value={appointmentForm.outline_id} onChange={(event) => setAppointmentForm({ ...appointmentForm, outline_id: event.target.value, cantico_suggerito: String(appointmentSpeaker.outlines.find((outline) => String(outline.id) === event.target.value)?.cantico_suggerito || "") })}>
                  <option value="">Seleziona uno schema</option>
                  <optgroup label="Schemi abilitati per l’oratore">
                    {appointmentSpeaker.outlines.map((outline) => <option key={outline.id} value={outline.id}>{outline.numero}. {outline.titolo}</option>)}
                  </optgroup>
                  {appointmentOtherOutlineOptions.length > 0 && <optgroup label="Altro schema dal catalogo ufficiale">
                    {appointmentOtherOutlineOptions.map((outline) => <option key={outline.id} value={outline.id}>{outline.numero}. {outline.titolo}</option>)}
                  </optgroup>}
                </select>
                <span className="mt-1 block text-xs font-normal text-gray-500">Gli altri schemi del catalogo sono disponibili come scelta eccezionale e non modificano la rosa dell’oratore.</span>
              </label>
              {speakerDirection === "outgoing" && (
                <label className="text-sm font-medium text-gray-700 sm:col-span-2">
                  Congregazione di destinazione
                  <select className="form-input mt-1" required value={appointmentForm.congregazione_ospitante_id} onChange={(event) => setAppointmentForm({ ...appointmentForm, congregazione_ospitante_id: event.target.value })}>
                    <option value="">Seleziona la destinazione</option>
                    {sharedDestinationCongregations.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
                  </select>
                  <span className="mt-1 block text-xs font-normal text-gray-500">Solo congregazioni collegate all’area {areas.find((area) => String(area.id) === String(selectedAreaId))?.nome || "selezionata"}.</span>
                </label>
              )}
              {speakerDirection === "incoming" && (
                <div className="rounded-lg border bg-gray-50 p-3 text-sm sm:col-span-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Congregazione ospitante</div>
                  <div className="mt-1 font-medium text-gray-900">{access?.congregation?.nome}</div>
                </div>
              )}
              <label className="text-sm font-medium text-gray-700 sm:col-span-2">
                Cantico suggerito
                <input type="number" min="1" inputMode="numeric" className="form-input mt-1" value={appointmentForm.cantico_suggerito} onChange={(event) => setAppointmentForm({ ...appointmentForm, cantico_suggerito: event.target.value })} placeholder="Numero del cantico (facoltativo)" />
                <span className="mt-1 block text-xs font-normal text-gray-500">Rimarrà salvato nell’appuntamento e potrà essere modificato dal dettaglio del calendario.</span>
              </label>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" disabled={saving} onClick={() => setAppointmentSpeaker(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Annulla</button>
              <button disabled={saving} className="btn-primary px-5">{saving ? "Salvataggio..." : "Salva appuntamento"}</button>
            </div>
          </form>
        </div>
      )}

      {editSpeakerModal && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40 sm:items-center sm:justify-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="speaker-edit-title" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !saving) setEditSpeakerModal(null);
        }}>
          <form onSubmit={saveSpeakerEdit} className="flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:max-w-3xl sm:rounded-2xl">
            <div className="flex shrink-0 items-start justify-between gap-4 border-b p-5">
              <div><h2 id="speaker-edit-title" className="text-xl font-semibold text-gray-900">Modifica oratore</h2><p className="mt-1 text-sm text-gray-600">{editSpeakerModal.speaker.congregazione_origine}</p></div>
              <button type="button" disabled={saving} onClick={() => setEditSpeakerModal(null)} className="min-h-11 min-w-11 rounded-lg p-2 text-gray-500 hover:bg-gray-100" aria-label="Chiudi modifica"><XMarkIcon className="mx-auto h-5 w-5" /></button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-5">
              <p className="mb-5 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">Le modifiche alla directory condivisa richiedono l’approvazione di un admin di un’altra congregazione. I dati attuali restano invariati fino all’approvazione.</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-medium text-gray-700">Nome<input required minLength={2} maxLength={100} className="form-input mt-1" value={editSpeakerModal.form.nome} onChange={(e) => updateEditSpeakerForm({ nome: e.target.value })} /></label>
                <label className="text-sm font-medium text-gray-700">Cognome<input required minLength={2} maxLength={100} className="form-input mt-1" value={editSpeakerModal.form.cognome} onChange={(e) => updateEditSpeakerForm({ cognome: e.target.value })} /></label>
                <label className="text-sm font-medium text-gray-700">Email<input type="email" className="form-input mt-1" value={editSpeakerModal.form.email} onChange={(e) => updateEditSpeakerForm({ email: e.target.value })} /></label>
                <label className="text-sm font-medium text-gray-700">Telefono<input type="tel" maxLength={40} className="form-input mt-1" value={editSpeakerModal.form.telefono} onChange={(e) => updateEditSpeakerForm({ telefono: e.target.value })} /></label>
                <label className="text-sm font-medium text-gray-700 sm:col-span-2">Nomina<select className="form-input mt-1" value={editSpeakerModal.form.qualifica} onChange={(e) => updateEditSpeakerForm({ qualifica: e.target.value })}><option value="anziano">Anziano</option><option value="servitore_ministero">Servitore di ministero</option></select></label>
              </div>
              <div className="mt-6"><h3 className="font-semibold text-gray-900">Schemi e cantici suggeriti</h3><p className="mt-1 text-sm text-gray-500">Un cantico predefinito per schema, facoltativo e modificabile in ogni appuntamento. Gli appuntamenti già salvati non cambiano.</p>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <select aria-label="Schema da aggiungere" className="form-input min-w-0 flex-1" value={editOutlineToAdd} onChange={(e) => setEditOutlineToAdd(e.target.value)}><option value="">Seleziona uno schema da aggiungere</option>{outlines.filter((outline) => !editSpeakerModal.form.outline_assignments.some((item) => Number(item.outline_id) === Number(outline.id))).map((outline) => <option key={outline.id} value={outline.id}>{outline.numero}. {outline.titolo}</option>)}</select>
                  <button type="button" disabled={!editOutlineToAdd} onClick={addEditSpeakerOutline} className="min-h-11 rounded-lg border border-primary-200 px-4 text-sm font-semibold text-primary-700 disabled:opacity-50">Aggiungi schema</button>
                </div>
                <div className="mt-3 space-y-2">{editSpeakerModal.form.outline_assignments.map((assignment) => {
                  const outline = outlines.find((item) => Number(item.id) === Number(assignment.outline_id)) || editSpeakerModal.speaker.outlines.find((item) => Number(item.id) === Number(assignment.outline_id));
                  return <div key={assignment.outline_id} className="grid gap-3 rounded-xl border bg-gray-50 p-3 sm:grid-cols-[1fr_9rem_auto] sm:items-center">
                    <div className="min-w-0 text-sm font-medium text-gray-800">{outline?.numero}. {outline?.titolo}</div>
                    <label className="text-xs font-medium text-gray-600">Cantico<input type="number" min="1" step="1" inputMode="numeric" placeholder="Facoltativo" className="form-input mt-1 bg-white" value={assignment.cantico_suggerito} onChange={(e) => updateEditSpeakerForm({ outline_assignments: editSpeakerModal.form.outline_assignments.map((item) => item.outline_id === assignment.outline_id ? { ...item, cantico_suggerito: e.target.value } : item) })} /></label>
                    <button type="button" onClick={() => updateEditSpeakerForm({ outline_assignments: editSpeakerModal.form.outline_assignments.filter((item) => item.outline_id !== assignment.outline_id) })} className="min-h-11 rounded-lg px-3 text-sm font-medium text-red-700 hover:bg-red-50">Rimuovi schema</button>
                  </div>;
                })}</div>
              </div>
            </div>
            <div className="flex shrink-0 flex-col-reverse gap-2 border-t bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end">
              <button type="button" disabled={saving} onClick={() => setEditSpeakerModal(null)} className="min-h-11 rounded-lg border px-4 text-sm font-medium text-gray-700">Annulla</button>
              <button disabled={saving || !editSpeakerModal.form.outline_assignments.length} className="btn-primary min-h-11 px-5">{saving ? "Salvataggio..." : "Proponi modifiche"}</button>
            </div>
          </form>
        </div>
      )}

      {historyModal && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40 p-0 sm:items-center sm:justify-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="speaker-history-title" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setHistoryModal(null);
        }}>
          <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:max-w-3xl sm:rounded-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-primary-700">{historyModal.outline ? `Cronostoria schema ${historyModal.outline.numero}` : "Cronostoria discorsi"}</p>
                <h2 id="speaker-history-title" className="mt-1 text-xl font-semibold text-gray-900">{historyModal.speaker.nome} {historyModal.speaker.cognome}</h2>
                <p className="mt-1 text-sm text-gray-600">{historyModal.speaker.congregazione_origine}{historyModal.outline ? ` · ${historyModal.outline.numero}. ${historyModal.outline.titolo}` : ""}</p>
              </div>
              <button type="button" onClick={() => setHistoryModal(null)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100" aria-label="Chiudi cronostoria">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            {!historyModal.loading && historyModal.stats && <div className="mt-5 space-y-4">
              <div className="rounded-xl border bg-gray-50 p-4"><h3 className="font-semibold text-gray-900">Statistiche{historyModal.outline ? ` dello schema ${historyModal.outline.numero}` : ""} · {access?.congregation?.nome}</h3><div className="mt-3 grid gap-3 sm:grid-cols-3"><div><div className="text-2xl font-semibold text-gray-900">{historyModal.stats.total_talks}</div><div className="text-sm text-gray-500">Discorsi svolti</div></div><div><div className="text-2xl font-semibold text-gray-900">{historyModal.stats.distinct_outlines}</div><div className="text-sm text-gray-500">Schemi trattati</div></div><div><div className={`w-fit rounded-full px-3 py-1.5 text-sm font-semibold ${feedbackMeta[historyModal.stats.feedback_color]?.className || "bg-gray-200 text-gray-600"}`}>{historyModal.stats.feedback_color ? `Gradimento: ${feedbackMeta[historyModal.stats.feedback_color].label}` : "Nessun feedback"}</div><div className="mt-1 text-xs text-gray-500">{historyModal.stats.feedback_count} valutazioni{historyModal.stats.feedback_count > 0 ? ` · media ${Number(historyModal.stats.feedback_average).toFixed(2)}/3` : ""}</div></div></div><p className="mt-3 text-xs text-gray-500">Valutazioni e note private della congregazione. La media dei colori non esclude automaticamente l’oratore.</p></div>
              {historyModal.stats.outlines.length > 0 && <div><h3 className="font-semibold text-gray-900">{historyModal.outline ? "Date di questa trattazione" : "Schemi trattati e date"}</h3><div className="mt-2 space-y-2">{historyModal.stats.outlines.map((outline) => <div key={outline.outline_id} className="rounded-xl border p-3"><div className="text-sm font-medium text-gray-800">{outline.numero}. {outline.titolo}</div><div className="mt-2 flex flex-wrap gap-2">{outline.dates.map((date, index) => <span key={`${date}-${index}`} className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600">{formatDate(date)}</span>)}</div></div>)}</div></div>}
            </div>}
            {historyModal.loading ? (
              <div className="py-10 text-center text-gray-500">Caricamento cronostoria...</div>
            ) : historyModal.entries.length ? (
              <div className="mt-5 space-y-3">
                {historyModal.entries.map((entry) => (
                  <article key={entry.id} className="rounded-xl border p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="font-semibold text-gray-900">{formatDate(entry.data)} · Schema {entry.numero}</div>
                        <div className="mt-1 text-sm text-gray-700">{entry.titolo}</div>
                        <div className="mt-1 text-sm text-gray-500">{entry.direzione === "outgoing" ? `Destinazione: ${entry.congregazione_ospitante}` : entry.direzione === "local" ? `Discorso locale presso ${entry.congregazione_ospitante}` : `Ospite presso ${entry.congregazione_ospitante}`}</div>
                        {entry.cantico_suggerito && <div className="mt-1 text-sm text-gray-500">Cantico suggerito: {entry.cantico_suggerito}</div>}
                      </div>
                      <span className={`w-fit rounded-full border px-2.5 py-1 text-xs font-medium ${directionMeta[entry.direzione].className}`}>{directionMeta[entry.direzione].label}</span>
                    </div>
                    <div className="mt-3 border-t pt-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Feedback interno · questo discorso</div>
                      <div className="mt-2 flex flex-wrap gap-2">{[3, 2, 1].map((color) => <button key={color} type="button" disabled={saving} aria-pressed={Number(feedbackDrafts[entry.id]?.colore ?? entry.colore) === color} onClick={() => setFeedbackDrafts((current) => ({ ...current, [entry.id]: { colore: color, note_consigli: current[entry.id]?.note_consigli ?? entry.note_consigli ?? "" } }))} className={`min-h-11 rounded-lg border px-4 text-sm font-medium ${feedbackMeta[color].className} ${Number(feedbackDrafts[entry.id]?.colore ?? entry.colore) === color ? "ring-2 ring-gray-600 ring-offset-2" : "border-transparent"}`}>{feedbackMeta[color].label}</button>)}</div>
                      <label className="mt-3 block text-sm font-medium text-gray-700">Note e consigli<textarea maxLength={5000} className="form-input mt-1 min-h-20" value={feedbackDrafts[entry.id]?.note_consigli ?? entry.note_consigli ?? ""} onChange={(e) => setFeedbackDrafts((current) => ({ ...current, [entry.id]: { colore: current[entry.id]?.colore ?? entry.colore, note_consigli: e.target.value } }))} /></label>
                      <button type="button" disabled={saving || !(feedbackDrafts[entry.id]?.colore ?? entry.colore)} onClick={() => saveFeedback(entry)} className="btn-primary mt-3 min-h-11 w-full px-4 sm:w-auto">{saving ? "Salvataggio..." : "Salva feedback"}</button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="mt-5 rounded-xl border border-dashed bg-gray-50 p-8 text-center text-gray-500">Questo oratore non ha ancora discorsi registrati nella cronostoria della congregazione.</div>
            )}
          </div>
        </div>
      )}

      {outlineSpeakersModal && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40 p-0 sm:items-center sm:justify-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="outline-speakers-title" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setOutlineSpeakersModal(null);
        }}>
          <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-3xl sm:rounded-2xl">
            <div className="sticky top-0 z-10 border-b bg-white p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-primary-700">Repertorio condiviso · {areas.find((area) => String(area.id) === String(selectedAreaId))?.nome || "Area attiva"}</p>
                  <h2 id="outline-speakers-title" className="mt-1 text-xl font-semibold text-gray-900">Schema {outlineSpeakersModal.outline.numero} · {outlineSpeakersModal.outline.titolo}</h2>
                  <p className="mt-1 text-sm text-gray-600">Ultime esecuzioni e feedback sono visibili solo per {access?.congregation?.nome || "la congregazione attiva"}.</p>
                </div>
                <button type="button" onClick={() => setOutlineSpeakersModal(null)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100" aria-label="Chiudi elenco oratori">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              {!outlineSpeakersModal.loading && <label className="mt-4 block text-sm font-medium text-gray-700 sm:max-w-xs">Ordina per
                <select aria-label="Ordina gli oratori dello schema" className="form-input mt-1 w-full" value={outlineSpeakerSort} onChange={(event) => setOutlineSpeakerSort(event.target.value)}>
                  <option value="least_recent">Mai trattato, poi meno recente</option>
                  <option value="most_recent">Più recente</option>
                  <option value="alphabetical">Ordine alfabetico</option>
                </select>
              </label>}
            </div>
            {outlineSpeakersModal.loading ? (
              <div className="p-10 text-center text-gray-500">Caricamento degli oratori dello schema...</div>
            ) : sortedOutlineSpeakers.length ? (
              <ul className="divide-y" aria-label="Oratori disponibili per lo schema selezionato">
                {sortedOutlineSpeakers.map((speaker) => {
                  const feedback = feedbackFromAverage(speaker.feedback_medio);
                  const hasHistory = Boolean(speaker.ultima_esecuzione_schema);
                  return <li key={speaker.id}>
                    <button type="button" onClick={() => {
                      const outline = outlineSpeakersModal.outline;
                      setOutlineSpeakersModal(null);
                      openSpeakerHistory(speaker, outline);
                    }} className="w-full p-4 text-left transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-600 sm:p-5">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900">{speaker.nome} {speaker.cognome}</div>
                          <div className="mt-1 text-sm text-gray-600">{speaker.congregazione_origine} · {speaker.qualifica === "anziano" ? "Anziano" : "Servitore di ministero"}</div>
                          {speaker.cantico_predefinito && <div className="mt-1 text-sm text-gray-500">Cantico abituale: {speaker.cantico_predefinito}</div>}
                        </div>
                        <span className="w-fit shrink-0 rounded-lg bg-primary-50 px-3 py-2 text-sm font-semibold text-primary-700">Apri cronostoria</span>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                        <span className={hasHistory ? "rounded-full bg-gray-100 px-2.5 py-1 text-gray-700" : "rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-800"}>
                          {hasHistory ? `Ultima volta: ${formatDate(speaker.ultima_esecuzione_schema)}` : "Mai trattato in questa congregazione"}
                        </span>
                        {hasHistory && <span className="text-gray-500">{speaker.esecuzioni_nella_congregazione} {Number(speaker.esecuzioni_nella_congregazione) === 1 ? "volta" : "volte"}</span>}
                        {feedback && <span className={`rounded-full px-2.5 py-1 font-medium ${feedback.className}`}>Gradimento: {feedback.label}</span>}
                      </div>
                    </button>
                  </li>;
                })}
              </ul>
            ) : (
              <div className="m-5 rounded-xl border border-dashed bg-gray-50 p-8 text-center text-gray-500">Nessun oratore dell'area ha ancora questo schema nel repertorio.</div>
            )}
          </div>
        </div>
      )}

      {selectedCalendarEntry && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40 p-0 sm:items-center sm:justify-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="public-talk-details-title" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setSelectedCalendarEntry(null);
        }}>
          <div className="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:max-w-xl sm:rounded-2xl" role="document">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-primary-700">Dettaglio discorso pubblico</p>
                <h2 id="public-talk-details-title" className="mt-1 text-xl font-semibold text-gray-900">{selectedCalendarEntry.nome} {selectedCalendarEntry.cognome}</h2>
                <p className="mt-1 text-sm text-gray-600">{selectedCalendarEntry.congregazione_origine}</p>
              </div>
              <button type="button" onClick={() => setSelectedCalendarEntry(null)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800" aria-label="Chiudi dettagli discorso">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {selectedCalendarEntry.stato === "declined" && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                <div className="font-semibold">Oratore non più disponibile — sostituzione necessaria</div>
                <p className="mt-1">La congregazione d'origine ha confermato la rimozione dell'oratore dalla directory.</p>
              </div>
            )}

            {!calendarEntryEditing ? (
              <>
                <dl className="mt-5 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                  <div className="rounded-lg bg-gray-50 p-3 sm:col-span-2">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Incarico</dt>
                    <dd className="mt-1 font-medium text-gray-900">{formatDate(selectedCalendarEntry.data)} · {String(selectedCalendarEntry.ora_inizio || "").slice(0, 5)}–{String(selectedCalendarEntry.ora_fine || "").slice(0, 5)}</dd>
                  </div>
                  <div className="rounded-lg border p-3 sm:col-span-2">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Tipo e congregazione</dt>
                    <dd className="mt-1 font-medium text-gray-900">{directionMeta[selectedCalendarEntry.direzione || "incoming"].label} · {selectedCalendarEntry.direzione === "outgoing" ? `destinazione: ${selectedCalendarEntry.congregazione_ospitante}` : `provenienza: ${selectedCalendarEntry.congregazione_origine}`}</dd>
                  </div>
                  <div className="rounded-lg border p-3 sm:col-span-2">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Schema {selectedCalendarEntry.numero}</dt>
                    <dd className="mt-1 font-medium text-gray-900">{selectedCalendarEntry.titolo}</dd>
                  </div>
                  <div className="rounded-lg border p-3 sm:col-span-2">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Cantico suggerito</dt>
                    <dd className="mt-1 font-medium text-gray-900">{selectedCalendarEntry.cantico_suggerito || "Non indicato"}</dd>
                  </div>
                  <div className="rounded-lg border p-3">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Telefono</dt>
                    <dd className="mt-1 font-medium text-gray-900">{selectedCalendarEntry.telefono ? <a className="text-primary-700 hover:underline" href={`tel:${selectedCalendarEntry.telefono}`}>{selectedCalendarEntry.telefono}</a> : "Non disponibile"}</dd>
                  </div>
                  <div className="rounded-lg border p-3">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Email</dt>
                    <dd className="mt-1 break-all font-medium text-gray-900">{selectedCalendarEntry.email ? <a className="text-primary-700 hover:underline" href={`mailto:${selectedCalendarEntry.email}`}>{selectedCalendarEntry.email}</a> : "Non disponibile"}</dd>
                  </div>
                </dl>
                <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
                  {!['cancelled', 'declined'].includes(selectedCalendarEntry.stato) && <button type="button" disabled={saving} onClick={() => setCalendarEntryEditing(true)} className="btn-primary min-h-11 px-4">Modifica appuntamento</button>}
                  {!['cancelled', 'declined'].includes(selectedCalendarEntry.stato) && <button type="button" disabled={saving} onClick={() => sendInvite(selectedCalendarEntry.id)} className="min-h-11 rounded-lg border border-primary-200 px-4 text-sm font-semibold text-primary-700 hover:bg-primary-50">{selectedCalendarEntry.invitation_sent_at ? "Invia aggiornamento ICS" : "Invia email + ICS"}</button>}
                  {!['cancelled', 'declined'].includes(selectedCalendarEntry.stato) && <button type="button" disabled={saving} onClick={() => {
                    if (window.confirm("Annullare questo discorso pubblico?")) {
                      cancelSchedule(selectedCalendarEntry).then((cancelled) => {
                        if (cancelled) setSelectedCalendarEntry(null);
                      });
                    }
                  }} className="min-h-11 rounded-lg px-4 text-sm font-semibold text-red-700 hover:bg-red-50">Annulla discorso</button>}
                </div>
              </>
            ) : (
              <form className="mt-5" onSubmit={(event) => { event.preventDefault(); saveCalendarEntry(); }}>
                <div className="rounded-xl border border-primary-100 bg-primary-50 p-3 text-sm text-primary-900">Modifica i dati dell'appuntamento senza cambiare la sua categoria: entrata, uscita o locale. Se l'oratore viene sostituito dopo l'invio dell'invito, il precedente riceverà l'annullamento ICS.</div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label className="text-sm font-medium text-gray-700 sm:col-span-2">Oratore
                    <select required className="form-input mt-1" value={calendarEntryForm?.speaker_id || ""} onChange={(event) => {
                      const nextSpeakerId = event.target.value;
                      const firstOutline = getSpeakerOutlineOptions(calendarEntryEligibleSpeakers, nextSpeakerId, outlines)[0];
                      setCalendarEntryForm((current) => ({ ...current, speaker_id: nextSpeakerId, outline_id: firstOutline ? String(firstOutline.id) : "", cantico_suggerito: firstOutline?.cantico_suggerito ? String(firstOutline.cantico_suggerito) : "" }));
                    }}>
                      <option value="">Seleziona l'oratore</option>
                      {calendarEntryEligibleSpeakers.map((speaker) => <option key={speaker.id} value={speaker.id}>{speaker.cognome} {speaker.nome} · {speaker.congregazione_origine}</option>)}
                    </select>
                  </label>
                  <label className="text-sm font-medium text-gray-700 sm:col-span-2">Schema
                  <select required className="form-input mt-1" value={calendarEntryForm?.outline_id || ""} onChange={(event) => {
                    const outline = calendarEntryOutlineOptions.find((item) => String(item.id) === event.target.value);
                    setCalendarEntryForm((current) => ({ ...current, outline_id: event.target.value, cantico_suggerito: outline?.cantico_suggerito ? String(outline.cantico_suggerito) : current.cantico_suggerito }));
                  }}>
                    <option value="">Seleziona uno schema</option>
                    <optgroup label="Schemi abilitati per l’oratore">
                      {calendarEntryOutlineOptions.map((outline) => <option key={outline.id} value={outline.id}>{outline.numero}. {outline.titolo}</option>)}
                    </optgroup>
                    {calendarEntryOtherOutlineOptions.length > 0 && <optgroup label="Altro schema dal catalogo ufficiale">
                      {calendarEntryOtherOutlineOptions.map((outline) => <option key={outline.id} value={outline.id}>{outline.numero}. {outline.titolo}</option>)}
                    </optgroup>}
                  </select>
                  <span className="mt-1 block text-xs font-normal text-gray-500">Puoi usare anche “Altro schema” dal catalogo ufficiale, senza alterare la rosa dell’oratore.</span>
                </label>
                  <label className="text-sm font-medium text-gray-700">Data<input required type="date" className="form-input mt-1" value={calendarEntryForm?.data || ""} onChange={(event) => setCalendarEntryForm((current) => ({ ...current, data: event.target.value }))} /></label>
                  <label className="text-sm font-medium text-gray-700">Cantico suggerito<input type="number" min="1" inputMode="numeric" className="form-input mt-1" value={calendarEntryForm?.cantico_suggerito || ""} onChange={(event) => setCalendarEntryForm((current) => ({ ...current, cantico_suggerito: event.target.value }))} placeholder="Facoltativo" /></label>
                  <label className="text-sm font-medium text-gray-700">Inizio<input type="time" className="form-input mt-1" value={calendarEntryForm?.ora_inizio || ""} onChange={(event) => setCalendarEntryForm((current) => ({ ...current, ora_inizio: event.target.value }))} /></label>
                  <label className="text-sm font-medium text-gray-700">Fine<input type="time" className="form-input mt-1" value={calendarEntryForm?.ora_fine || ""} onChange={(event) => setCalendarEntryForm((current) => ({ ...current, ora_fine: event.target.value }))} /></label>
                  <label className="text-sm font-medium text-gray-700 sm:col-span-2">Note operative<textarea maxLength={3000} className="form-input mt-1 min-h-20" value={calendarEntryForm?.note || ""} onChange={(event) => setCalendarEntryForm((current) => ({ ...current, note: event.target.value }))} /></label>
                </div>
                <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button type="button" disabled={saving} onClick={() => setCalendarEntryEditing(false)} className="min-h-11 rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50">Annulla</button>
                  <button disabled={saving} className="btn-primary min-h-11 px-5">{saving ? "Salvataggio..." : "Salva modifiche"}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {section === "approvals" && (
        <div className="space-y-3">
          {requests.map((request) => {
            const presentation = getApprovalRequestPresentation(request);
            return <div key={request.id} className="bg-white border rounded-xl p-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{presentation.title}</div>
                <div className="mt-1 text-sm text-gray-500">{presentation.detail}</div>
                <div className="text-sm text-gray-600">Proposta da {request.proponente_nome} · {request.congregazione_proponente}</div>
                {request.tipo === "remove_speaker" && <div className="mt-1 text-sm text-gray-500">Conferma richiesta a un altro admin o coordinatore di {request.speaker_congregazione_origine || request.congregazione_proponente}.</div>}
                {request.tipo === "add_speaker" && <div className="mt-3 rounded-lg border bg-gray-50 p-3">
                  <h3 className="text-sm font-semibold text-gray-900">Oratore proposto</h3>
                  <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
                    <div><dt className="text-xs text-gray-500">Nome e cognome</dt><dd className="break-words text-gray-800">{request.payload?.nome} {request.payload?.cognome}</dd></div>
                    <div><dt className="text-xs text-gray-500">Nomina</dt><dd className="text-gray-800">{request.payload?.qualifica === "anziano" ? "Anziano" : "Servitore di ministero"}</dd></div>
                    <div><dt className="text-xs text-gray-500">Congregazione di origine</dt><dd className="text-gray-800">{request.payload_congregazione_origine || request.congregazione_proponente}</dd></div>
                    <div><dt className="text-xs text-gray-500">Schemi proposti</dt><dd className="text-gray-800">{(request.payload?.outline_ids || []).length}</dd></div>
                  </dl>
                </div>}
                {request.tipo === "bulk_add_speakers" && <div className="mt-3 rounded-lg border bg-gray-50 p-3">
                  <h3 className="text-sm font-semibold text-gray-900">Oratori proposti</h3>
                  <div className="mt-2 flex flex-wrap gap-2">{(request.payload?.speakers || []).map((speaker, index) => <span key={`${speaker.nome}-${speaker.cognome}-${index}`} className="rounded-md bg-white px-2 py-1 text-sm text-gray-700 ring-1 ring-gray-200">{speaker.nome} {speaker.cognome}</span>)}</div>
                </div>}
                {request.tipo === "update_speaker" && <div className="mt-3 rounded-lg border bg-gray-50 p-3">
                  <h3 className="text-sm font-semibold text-gray-900">Dati proposti — attivi solo dopo approvazione</h3>
                  <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
                    <div><dt className="text-xs text-gray-500">Nome e cognome</dt><dd className="break-words text-gray-800">{request.payload.nome} {request.payload.cognome}</dd></div>
                    <div><dt className="text-xs text-gray-500">Nomina</dt><dd className="text-gray-800">{request.payload.qualifica === "anziano" ? "Anziano" : "Servitore di ministero"}</dd></div>
                    <div><dt className="text-xs text-gray-500">Email</dt><dd className="break-all text-gray-800">{request.payload.email || "Non indicata"}</dd></div>
                    <div><dt className="text-xs text-gray-500">Telefono</dt><dd className="break-words text-gray-800">{request.payload.telefono || "Non indicato"}</dd></div>
                  </dl>
                  <div className="mt-3 space-y-1.5">{(request.payload.outline_assignments || (request.payload.outline_ids || []).map((id) => ({ outline_id: id }))).map((assignment) => {
                    const outline = outlines.find((item) => Number(item.id) === Number(assignment.outline_id));
                    return <div key={assignment.outline_id} className="rounded-md border bg-white p-2 text-sm"><div className="font-medium text-gray-800">Schema {outline?.numero || assignment.outline_id}{outline?.titolo ? ` · ${outline.titolo}` : ""}</div><div className="mt-1 text-xs text-gray-500">Cantico: {assignment.cantico_suggerito || "non indicato"}</div></div>;
                  })}</div>
                </div>}
              </div>
              {request.can_review && (
                <div className="flex shrink-0 gap-2">
                  <button disabled={saving} onClick={() => review(request.id, "rejected")} className="px-3 py-2 border border-red-200 text-red-700 rounded-lg">Rifiuta</button>
                  <button disabled={saving} onClick={() => review(request.id, "approved")} className="btn-primary px-3">Approva</button>
                </div>
              )}
            </div>;
          })}
          {!requests.length && <div className="bg-white border rounded-xl p-8 text-center text-gray-500">Nessuna modifica in attesa.</div>}
        </div>
      )}
    </div>
  );
};

export default DiscorsiPubblici;
